//#region Imports
import { AbsgArrayHelper, CacheService, CamelMapHelper, LogService } from '@absg/angular-common';
import { ODataResult } from '@absg/angular-components';
import { DateFormat, DateTimeHelper } from '@absg/angular-platform';
import { EmrPracticeConfig, MedOrder, MedOrderDetail, MedOrderResponse, Patient as MedOrderPatient, PatientsWithMedOrders } from '@absg/api-emr-integration';
import {
  BinProductDetail, FormularyOrderDetailLite, GetAssignedItemByOrderMatchingRequest, MedicalEquivalentApi, OrderWithMatchingDrugs, PracticeEMRMedDetail, PracticeFormularyApi, ProductDetail,
  RuleProductSetting,
} from '@absg/api-formulary';
import { UserClaims } from '@absg/api-identity';
import {
  Activity,
  ActivityApi,
  AutoConfigResponse,
  AutoConfigureRequest,
  BinGroupInventoryItem,
  CheckFeatureRequest,
  CheckFeatureResponse,
  CreateLotNumber,
  DispenseApi,
  DispenseDetailSnapshot,
  DispenseSnapshot,
  EventDetailInfo,
  InventoryItem as APIInventoryItem,
  InventoryItemProduct,
  InventoryItemSummary,
  LastAttendingPhysicianDetails,
  MedOrderDetail as ApiInventoryMedOrderDetail,
  MedOrderDetailConfig,
  MedOrderDispenseItem,
  MedOrderTransactionActivityDetail,
  ReturnWasteLot,
  Transaction,
  TransactionApi,
  TransactionRequest,
} from '@absg/api-inventory';
import { LookupDetail } from '@absg/api-lookup';
import { PatientLite, PracticeClinicDetail } from '@absg/api-party';
import {
  BaseService, CacheKeys, CalculationHelper, ConversionHelper, DispenseDisplay, DispenseItem, DispenseSnapshotViewModel, DispenseTypes,
  FeatureEnum, FormularyConversionHelpers, InventoryDisplay, InventoryItem, InventoryTransactionTypes, ItemDisplay, LocationType, LotDetail,
  Manufacturer, MedOrderHelperService, NdcDetail, NimAlertService, PartyService, Patient, PatientDispenseItemViewModel,
  PatientMedOrderStatus, PatientService as PartyPatientService, Physician, PickListItem, RequiredNote, SessionService,
  EmrVendorEnum,
} from '@absg/nim-client-shared';
import { Injectable } from '@angular/core';
import { CommonDataService } from 'core.services/common-data.service';
import { StationInfoService } from 'core.services/station-info.service';
import { MedOrderMatchEnum } from 'dispense.enums/med-order-match.enum';
import { TransactionStatus } from 'dispense.enums/transaction-status.enum';
import { DispenseSelectedGroup } from 'dispense.models/dispense-selected-group.model';
import { DispenseStateService } from 'dispense.models/dispense-state.service';
import { DispenseDetailSnapshotViewModel } from 'dispense.models/dispenseDetailSnapshot.model';
import { DisplayTabByEmrConfig } from 'dispense.models/display-tab-by-emr-config.model';
import { ExtractedAccessPoint } from 'dispense.models/extractedAccessPoint';
import { FutureMedOrderOptions } from 'dispense.models/future-med-order-options.model';
import { IdentifierDetail } from 'dispense.models/identifier-detail.model';
import { PrintingDataModel } from 'dispense.models/printing-data.model';
import { ManagePositionHelper } from 'inventory.root/shared/helpers/manage-position-helper';
import { BehaviorSubject, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { InventoryActivityTypes } from 'shared.enums/inventory-activity-types.enum';
import { DiagnosisCode } from 'shared.models/diagnosis-code.model';
import { CleanupEvents } from 'shared.root/constants/clen-up-events';
import { PatientOwnedItemHelper } from 'shared.root/helpers/patient-owned-item-helper';
import { MedOrderDispenseDetail } from 'shared.root/models/med-order-detail';
import { WitnessItem } from 'shared.root/models/witness-base.model';
import { EmrService } from 'shared.root/services/emr.service';
import { LookupService } from 'shared.root/services/lookup.service';
import { SettingsService } from 'shared.root/services/settings.service';
import { FormularyManagementService } from 'shared.root/services/state-management/formulary-management.service';
import { DiagnosisCodeService } from 'shared.services/diagnosis-code.service';
import { ElectronApplicationService } from 'shared.services/electron-application.service';
import { FormularyService } from 'shared.services/formulary.service';
import { InventoryService } from 'shared.services/inventory.service';
import { PatientService } from 'shared.services/patient.service';
import { PhysicianService } from 'shared.services/physician.service';

import { DispenseNames } from '../constants/dispense-names';
import { DispenseTabs } from '../constants/dispense-tabs';
import { MedOrderDispenseHelper } from '../helpers/med-order-dispense.helper';
import { DispenseCommonOperationsService } from './dispense-common-operations.service';
import { DispensePointInTimeDataService } from './dispense-point-in-time-data.service';
//#endregion Imports

@Injectable({ providedIn: 'root' })
export class DispenseDataService extends BaseService {
  private practiceClinicInfo: BehaviorSubject<PracticeClinicDetail>;
  private ipcRenderer: Electron.IpcRenderer;
  private diagnosisCodes: Observable<ODataResult<DiagnosisCode>>;
  private patients: Observable<Patient[]>;
  private inventoryItems = new BehaviorSubject<InventoryItem[]>([]);
  private vpmLpmDoorNumber = 99999; // there can only be 1 LPM or VPM, no multiples
  // This object stores a list of items <containerId, [itemId]> for dispense from history
  // TODO:Rip this out when we have a model that makes sense
  private historyTracker = new Map<number, [string]>();
  private dispenseWorkflowNavigatedTab: DispenseTabs;

  private dispenseState: DispenseStateService;
  // This object stores a list of items <containerId, [itemId]> for dispense from history
  // TODO:Rip this out when we have a model that makes sense
  binsTracker = new Map<number, [string]>();

  currentTransaction = new BehaviorSubject<Transaction>(null);
  dispenseItems: DispenseItem[] = [];
  selectedDiagnosisCodes: DiagnosisCode[] = [];
  selectedDispenseItem: DispenseItem;
  selectedDispenseDisplay: DispenseDisplay;
  emrPracticeConfigDetail: EmrPracticeConfig;
  manufacturers: Manufacturer[] = [];
  // Med Order Variables
  medOrderDispenseItems: DispenseSelectedGroup[] = [];
  originalMedOrderDispenseItems: DispenseSelectedGroup[] = [];
  medOrderDispenseDetailList = new BehaviorSubject<MedOrderDispenseDetail[]>([]);
  medOrderPatient: MedOrderPatient;
  futureMedOrderOptions: FutureMedOrderOptions;

  historyWorkflowSelectedItems: PatientDispenseItemViewModel[] = [];

  isInventoryCached = false;
  isStationConfigurationCached = false;
  selectedAttendingPhysician: Physician;
  serviceDate: Date;
  userPatientSearchText: string = null;

  ruleSettings: RuleProductSetting[];
  patientOwnedItemTypeList: LookupDetail[] = [];
  private dispenseRequestCounter = 0;
  isSaveDispenseRequestsCompleted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  isSingleItemMultiMarEnabled = true; // single item multi MAR enabled
  mappedPatientList: PatientLite[] = [];
  isItemDispensed = false;
  isPhysicianSelected = false;
  isScheduledOrderVendor = false;
  isResetDispenseItemSelection = new BehaviorSubject<boolean>(false);
  medOrderDiagnosisCodes: DiagnosisCode[] = [];
  transactionId = 0;

  constructor(
    private diagnosisCodeService: DiagnosisCodeService,
    private formularyService: FormularyService,
    private formularyManagementService: FormularyManagementService,
    private inventoryService: InventoryService,
    private patientService: PatientService,
    private electronApplicationService: ElectronApplicationService,
    private physicianService: PhysicianService,
    private stationInfoService: StationInfoService,
    private activityApi: ActivityApi,
    private transactionApi: TransactionApi,
    private dispenseApi: DispenseApi,
    private sessionService: SessionService,
    private settingService: SettingsService,
    private lookupService: LookupService,
    private nimAlertService: NimAlertService,
    private dispensePointInTimeDataService: DispensePointInTimeDataService,
    private dispenseCommonOperationsService: DispenseCommonOperationsService,
    private medicalEquivalentApi: MedicalEquivalentApi,
    private emrService: EmrService,
    private cacheService: CacheService,
    private commonDataService: CommonDataService,
    private partyPatientService: PartyPatientService,
    private partyService: PartyService,
    private medOrderHelperService: MedOrderHelperService,
    private logService: LogService,
    private practiceFormularyApi: PracticeFormularyApi,
  ) {
    super(logService);
    // don't listen for events if not running inside electron
    if (this.electronApplicationService.isRunningElectron()) {
      // NOTE: These events are defined in electron-cleanup.js
      this.ipcRenderer = this.electronApplicationService.getIpcRenderer();
      this.listen();
    }
    this.practiceClinicInfo = this.stationInfoService.practiceClinicDetail;
    this.dispenseState = new DispenseStateService();
  }

  get selectedPatient(): Patient {
    return this.stationInfoService.selectedPatient;
  }

  set selectedPatient(currentPatient: Patient) {
    if (!AbsgArrayHelper.isNil(currentPatient)) {
      this.handleMessage(`Patient Selected ${currentPatient.id}`);
      this.clearDispenseData();
      this.closeDispenseTransaction();
    }
    this.stationInfoService.selectedPatient = currentPatient;
  }

  setupState(): Observable<void> {
    this.dispenseState = new DispenseStateService();
    return this.stationInfoService.getCachedInventoryItems().pipe(take(1),
      concatMap((items) => {
        const supplierProductInclusionIdList = this.getSupplierProductInclusionIdList(items);
        const ids = this.getPositionContainerIds();
        this.settingService.stationContainerId = this.stationInfoService.station?.value?.id;
        const patientIds = this.getPatientIdList(items);
        const patientApiCall = this.partyPatientService.getPatientListByIds(patientIds).pipe(take(1));

        return forkJoin([
          this.formularyManagementService.searchFormularyItemsBasedOnInventory(supplierProductInclusionIdList, ''),
          this.lookupService.getPatientOwnedItemTypeDetailList(),
          patientApiCall,
          this.partyService.getManufacturers(),
          this.practiceFormularyApi.getPracticeEMRMedIds(),
          this.settingService.populateXrefs(ids, supplierProductInclusionIdList),
        ]).pipe(
          take(1),
          map(([foundProducts, patientOwnedItemTypes, patientList, manufacturerList, practiceEmrMedDetailList]) => {
            this.patientOwnedItemTypeList = patientOwnedItemTypes;
            if (manufacturerList != null) {
              this.manufacturers = CamelMapHelper.camelizeKeys(manufacturerList);
            }
            if (patientList != null) {
              this.mappedPatientList = CamelMapHelper.camelizeKeys(patientList) as PatientLite[];
            }
            this.dispenseState.assignedItems = foundProducts;
            const inventoryItemsList = this.processInventoryItems(foundProducts, practiceEmrMedDetailList);
            AbsgArrayHelper.each(inventoryItemsList, item => {
              item.patientInfo = AbsgArrayHelper.find(this.mappedPatientList, patientInfo => patientInfo.patientId === item.patientId);
            });
            this.setPositionRules(inventoryItemsList);
            this.dispenseState.allInventoryItems = AbsgArrayHelper.cloneDeep(inventoryItemsList);
            this.dispenseState.products = AbsgArrayHelper.cloneDeep(inventoryItemsList);
          }),
          catchError(error => {
            this.nimAlertService.showErrorAlert('Error setting state for Dispense.');
            this.handleError(error);
            return of(null);
          }),
        );
      }));
  }

  getPatientIdList(inventoryItem: InventoryItemSummary[]): number[] {
    if (!inventoryItem) {
      return [];
    }
    const patientIds = inventoryItem
      .filter(position => position?.patientId)
      .map(position => position.patientId);

    return patientIds;
  }

  getInventoryItems(): InventoryItem[] {
    if (this.dispenseState) {
      return this.dispenseState.products;
    }
    return [];
  }

  getAllItems(): InventoryItem[] {
    if (this.dispenseState) {
      return this.dispenseState.allInventoryItems;
    }
    return [];
  }

  setAllItems(items: InventoryItem[]): void {
    this.dispenseState.allInventoryItems = items;
  }

  getPracticeExpirationDateWarningDay(): number {
    return this.stationInfoService.getPracticeExpirationDateWarningDay();
  }

  getPracticeMinimumCharacterSearch(): number {
    return this.stationInfoService.getPracticeMinimumCharacterSearch();
  }

  fetchMedOrderForPatient(patientId: string, patientPartyId?: number | null, isScheduledOrder = false): Observable<string> {
    let medOrderResponse$: Observable<MedOrderResponse>;
    if (isScheduledOrder) {
      medOrderResponse$ = this.emrService.getMedOrderByPatientPartyId(patientPartyId, this.futureMedOrderOptions?.clinicId, this.futureMedOrderOptions?.medOrderDate);
    } else {
      medOrderResponse$ = this.emrService.getMedOrderByPatientNumber(patientId, this.futureMedOrderOptions?.clinicId, this.futureMedOrderOptions?.medOrderDate);
    }
    return medOrderResponse$.pipe(
      take(1),
      concatMap((response: MedOrderResponse) => {
        if (response && !response.isError) {
          if (response.medOrder && response.medOrder.details && response.medOrder.details.length > 0) {
            this.medOrderPatient = response.medOrder.patient;
            const medOrderDetailIdList: number[] = response.medOrder.details.map(detail => detail.medOrderDetailId);
            return this.inventoryService.getDispenseInfoByMedOrderItems(medOrderDetailIdList).pipe(
              take(1),
              map((dispenseData: MedOrderTransactionActivityDetail[]) => {
                this.medOrderDispenseDetailList.next(this.processMedOrderDetailData(response.medOrder, dispenseData));
                return '';
              }));
          } else {
            return of('No Med Order found for the patient.');
          }
        } else {
          return of(response.errorText);
        }
      }),
      catchError(error => this.handleErrorResponse(error)));
  }

  showTabBasedOnEmrConfig(): Observable<DisplayTabByEmrConfig> {
    return forkJoin([
      this.emrService.getEmrConfig(false).pipe(take(1)),
      this.getJcodeExclusionList().pipe(take(1)),
      this.searchInventoryItemsArray('').pipe(take(1)),
      this.loadBinGroups(),
    ]).pipe(
      take(1),
      concatMap(([config, excludedJcodeList, inventoryList, __]) => {
        if (config && config.enabled) {
          const emrName = (config as EmrPracticeConfig).emr;
          const isScheduledOrder = emrName === DispenseNames.emrNameScheduledOrder;
          const medOrderName: string = isScheduledOrder ? 'Scheduled Orders' : emrName; // using Portal name to indicate origin
          return this.fetchMedOrderForPatient(this.selectedPatient.patientId, this.selectedPatient.patientPartyId, isScheduledOrder).pipe(
            take(1),
            concatMap((errorString: string) => {
              if (errorString.length === 0) {
                const orderDetails = this.mapOrdersToFormularyOrderDetailLite();
                return this.getAssignedItemsByOrderMatching(orderDetails, inventoryList, config.filterByJCode, this.selectedPatient.id).pipe(
                  take(1),
                  concatMap((ordersWithmatchingItems: OrderWithMatchingDrugs[]) => {
                    const ordersWithDrugs = AbsgArrayHelper.filter(ordersWithmatchingItems, omi => omi.matchingDrugs?.length > 0);
                    if (config.autoConfigureEnabled && ordersWithDrugs.length > 0) {
                      const request = this.mapAutoConfigureRequest(ordersWithDrugs, excludedJcodeList, inventoryList);
                      return this.autoConfigure(request).pipe(
                        take(1),
                        map((response: AutoConfigResponse) => {
                          this.processAutoConfigResponse(response);
                          return this.createDisplayTabByConfigModel(true, true, DispenseTabs.MEDORDER_TAB, false, '', false, '');
                        }),
                        catchError((err) => {
                          this.logService.logError(err);
                          return of(this.createDisplayTabByConfigModel(true, false, DispenseTabs.INVENTORY_TAB, false, '', true,
                            `An error prevented retrieving ${this.getOrderLabelName()} information from ${medOrderName}`));
                        }));
                    } else {
                      return of(this.createDisplayTabByConfigModel(true, true, DispenseTabs.MEDORDER_TAB, false, '', false, ''));
                    }
                  }));
              } else {
                let errorText = '';
                let displayTabModel: DisplayTabByEmrConfig;
                if (errorString.toLowerCase().includes('no med order found for the patient.') || errorString.toLowerCase().includes('unable to find appointments.')) {
                  // eslint-disable-next-line max-len
                  let message = `There are no approved ${this.getOrderLabelName()}s for <b>${this.selectedPatient.lastName}</b>, <b>${this.selectedPatient.firstName}</b> (ID <strong>${this.selectedPatient.patientId}</strong>, DOB ${DateTimeHelper.formatDate(this.selectedPatient.dateOfBirth, DateFormat.USADateOnly).toString().bold()})`;
                  if (!this.isScheduledOrderVendor) {
                    message = `${message} in ${medOrderName}`;
                  }
                  displayTabModel = this.createDisplayTabByConfigModel(true, false, DispenseTabs.INVENTORY_TAB, true, message, false, '');
                } else if (errorString.toLowerCase().includes('could not retrieve an auth token from') ||
                  errorString.toLowerCase().includes('invalid practice pin') ||
                  errorString.toLowerCase().includes('incorrect pin') ||
                  errorString.toLowerCase().includes('patient number is invalid')) {
                  errorText = `An incorrect PIN prevented retrieving ${this.getOrderLabelName()} information from ${medOrderName}.`;
                  displayTabModel = this.createDisplayTabByConfigModel(true, false, DispenseTabs.INVENTORY_TAB, false, '', true, errorText);
                } else if (errorString.toLowerCase().includes('no endpoint') ||
                  errorString.toLowerCase().includes('page not found') ||
                  errorString.toLowerCase().includes('no connection') ||
                  errorString.toLowerCase().includes('unavailable.aspx')) {
                  errorText = `An incorrect URL prevented retrieving ${this.getOrderLabelName()} information from ${medOrderName}.`;
                  displayTabModel = this.createDisplayTabByConfigModel(true, false, DispenseTabs.INVENTORY_TAB, false, '', true, errorText);
                } else if (errorString.toLowerCase().includes('exception occurred while deserializing') ||
                  errorString.toLowerCase().includes('patient id not found in emr')) { // patient is not found
                  // eslint-disable-next-line max-len
                  const message = `${this.selectedPatient.lastName.bold()}, ${this.selectedPatient.firstName.bold()} (ID ${this.selectedPatient.patientId.bold()}, DOB ${DateTimeHelper.formatDate(this.selectedPatient.dateOfBirth, DateFormat.USADateOnly).toString().bold()}) was not found in ${medOrderName}`;
                  displayTabModel = this.createDisplayTabByConfigModel(true, false, DispenseTabs.INVENTORY_TAB, true, message, false, '');
                }
                return of(displayTabModel);
              }
            }),
            catchError((err) => {
              this.logService.logError(err);
              return of(this.createDisplayTabByConfigModel(true, false, DispenseTabs.INVENTORY_TAB, false, '', true,
                `An error prevented retrieving ${this.getOrderLabelName()} information from ${medOrderName}`));
            }));
        } else {
          return of(this.createDisplayTabByConfigModel(false, false, DispenseTabs.INVENTORY_TAB, false, '', false, ''));
        }
      }),
      catchError(() => of(this.createDisplayTabByConfigModel(false, false, DispenseTabs.INVENTORY_TAB, false, '', false, ''))),
    );
  }

  createDisplayTabByConfigModel(isMedOrderTabVisible: boolean, isMedOrderTabEnabled: boolean, tabToDisplay: string,
    showConfirmationModal: boolean, confirmationModalMessage: string, showAlert: boolean, alertText: string): DisplayTabByEmrConfig {
    return {
      alertText,
      confirmationModalMessage,
      isMedOrderTabEnabled,
      isMedOrderTabVisible,
      showAlert,
      showConfirmationModal,
      tabToDisplay,
    } as DisplayTabByEmrConfig;
  }

  getOrderLabelName(): string {
    if (this.isScheduledOrderVendor) {
      return DispenseNames.scheduledDispense;
    }
    return DispenseNames.medOrder;
  }

  autoConfigureMedOrders(): void {
  }

  isItemAlreadyInUse(item: InventoryDisplay): boolean {
    return this.dispenseItems.some(di =>
      di.inventoryItem.supplierProductInclusionId === item.defaultInventoryItem.supplierProductInclusionId &&
      di.inventoryItem.getPositionName() === item.defaultInventoryItem.getPositionName());
  }

  configureItem(item: InventoryDisplay, momi: MedOrderDispenseDetail, amountToFulfill: number): void {
    const positionNDCs = this.getContainerNDCsWithQtyOnHand(item.defaultInventoryItem.getContainerId());
    if ((item.isBin && positionNDCs.length > 1) ||
      (item.defaultInventoryItem.multiLotYN && this.hasItemMultipleLots(item.defaultInventoryItem))) {
      // configure it as configured in mini calculator
      this.configureItemAsCalculator(momi, item, amountToFulfill, true);
    } else {
      // configure it as configured in regular calculator
      this.configureItemAsCalculator(momi, item, amountToFulfill, false);
    }
  }

  getAmountToFulfill(medOrderDetail: MedOrderDispenseDetail, dispenseUnit = ''): number {
    if (medOrderDetail.dispensedUnit.toUpperCase() === medOrderDetail.getMedOrderDoseUnit().toUpperCase()) {
      const amountInMedOrderDoseUnit = CalculationHelper.subtract(medOrderDetail.getMedOrderDoseAmount(), medOrderDetail.dispensedAmount);
      if (dispenseUnit.length === 0 || medOrderDetail.getMedOrderDoseUnit().toUpperCase() === dispenseUnit.toUpperCase()) {
        return amountInMedOrderDoseUnit;
      } else {
        return ConversionHelper.convertQuantityByUnitName(amountInMedOrderDoseUnit, medOrderDetail.getMedOrderDoseUnit(), dispenseUnit);
      }
    } else {
      // skip the med order in case there is past dispense and past dispensed unit is not same or convertible with med order dose unit
      return 0;
    }
  }

  configureItemAsCalculator(medOrderDetail: MedOrderDispenseDetail, item: InventoryDisplay, amountToFulfill: number,
    isMini: boolean): void {
    // final check if that item is already configured
    if (!this.isItemAlreadyInUse(item)) {
      const dispenseItem = new DispenseItem();
      dispenseItem.dispenseOrigin = DispenseTabs.MEDORDER_TAB;
      // amount already converted in dispense unit
      item.defaultInventoryItem.quantitySelected = amountToFulfill;
      dispenseItem.inventoryItem = item.defaultInventoryItem;
      dispenseItem.fromCabinetQuantity = 0;
      dispenseItem.fromUnderHoodQuantity = 0;
      dispenseItem.toCabinetQuantity = 0;
      dispenseItem.toUnderHoodQuantity = 0;
      dispenseItem.wasteQuantity = 0;
      if (!isMini) {
        const itemConfigs: number[] = this.calculateItemConfigForRegularCalculator(item);
        if (itemConfigs && itemConfigs.length > 0) {
          dispenseItem.fromCabinetQuantity = itemConfigs[0];
          dispenseItem.fromUnderHoodQuantity = itemConfigs[1];
          dispenseItem.toCabinetQuantity = itemConfigs[2];
          dispenseItem.toUnderHoodQuantity = itemConfigs[3];
          dispenseItem.wasteQuantity = itemConfigs[4];
          dispenseItem.inventoryItem.quantitySelectedFromCabinet = dispenseItem.fromCabinetQuantity;
          dispenseItem.inventoryItem.quantitySelectedFromUnderhood = dispenseItem.fromUnderHoodQuantity;
          dispenseItem.returnToCabinet = dispenseItem.inventoryItem.dispenseTypeId !== DispenseTypes.ReturnToUnderHood;
        } else {
          // exception # 7 - fraction amount for whole dispense item
          return;
        }
      }
      this.addUpdateDispenseItem(dispenseItem);
      this.configureMedOrder(dispenseItem, medOrderDetail);
    }
  }

  calculateItemConfigForRegularCalculator(itemDisplay: InventoryDisplay): number[] {
    //  As regular calculator, dispenseItem needs above properties to be calculated
    // calculate quantities from underhood and cabinet
    const item = itemDisplay.defaultInventoryItem;
    const dispenseQuantity = item.quantitySelected;
    let fromCabinetQuantity = 0;
    let fromUnderHoodQuantity = 0;
    let toCabinetQuantity = 0;
    let toUnderHoodQuantity = 0;
    let wasteQuantity = 0;
    let invUnits = 0;
    const uthQuantity = item.quantityUnderHood;
    if (uthQuantity > 0) {
      fromUnderHoodQuantity = dispenseQuantity > uthQuantity ? uthQuantity : dispenseQuantity;
      fromCabinetQuantity = CalculationHelper.formatQuantity(dispenseQuantity - fromUnderHoodQuantity);
    } else {
      fromCabinetQuantity = dispenseQuantity;
      fromUnderHoodQuantity = 0;
    }
    // calculate fraction
    // const invItem = dispenseItem.inventoryItem;
    const fractionFromCabinet = this.calculateFraction(fromCabinetQuantity, item.quantityInCabinet,
      item.dispenseUOMConversionFactor, item.inventoryUOMConversionFactor);
    const fractionFromUnderHood = fromUnderHoodQuantity > 0 ? this.calculateFraction(fromUnderHoodQuantity, item.quantityUnderHood,
      item.dispenseUOMConversionFactor, item.inventoryUOMConversionFactor) : 0;
    // add the fraction coming out when taking the required amount
    fromCabinetQuantity += fractionFromCabinet;
    fromUnderHoodQuantity += fractionFromUnderHood;
    const invUnitsFromCabinet = CalculationHelper.convertToQuantity(CalculationHelper.subtract(fromCabinetQuantity, fractionFromCabinet),
      item.dispenseUOMConversionFactor, item.inventoryUOMConversionFactor);
    const invUnitsFromUth = CalculationHelper.convertToQuantity(CalculationHelper.subtract(fromUnderHoodQuantity, fractionFromUnderHood),
      item.dispenseUOMConversionFactor, item.inventoryUOMConversionFactor);

    const fractionAmount = CalculationHelper.formatQuantity(fractionFromCabinet + fractionFromUnderHood);
    invUnits = CalculationHelper.add(invUnitsFromCabinet, invUnitsFromUth);

    if (fractionAmount > 0) {
      switch (item.dispenseTypeId) {
        case DispenseTypes.WholeDispense:
          // check for exception # 7 - fraction amount for whole dispense item
          return null;
        case DispenseTypes.AutoCalculateWaste:
          wasteQuantity = fractionAmount;
          break;
        case DispenseTypes.ReturnToUnderHood:
          toUnderHoodQuantity = fractionAmount;
          break;
        case DispenseTypes.ReturnToPosition:
          toCabinetQuantity = fractionAmount;
          break;
      }
    }
    return [fromCabinetQuantity, fromUnderHoodQuantity, toCabinetQuantity, toUnderHoodQuantity, wasteQuantity, invUnits];
  }

  calculateFraction(fromPositionQuantity: number, quantityInPosition: number,
    dispenseFactor: number, inventoryFactor: number): number {
    return fromPositionQuantity > 0 && (quantityInPosition - fromPositionQuantity) > 0 ? CalculationHelper.formatQuantity(
      CalculationHelper.modulus(CalculationHelper.subtract(quantityInPosition, fromPositionQuantity), (dispenseFactor / inventoryFactor))) : 0;
  }

  setDispenseSettings(): void {
    this.dispenseItems.forEach((dispenseItem) => {
      this.setInventoryItemSettings(dispenseItem.inventoryItem);
    });
  }

  setInventoryItemDispenseTypeIdFromPosition(inventoryItem: InventoryItem): void {
    const containerId = inventoryItem.getContainerId();
    inventoryItem.dispenseTypeId = this.settingService.getDispenseTypeIdSetting(containerId);
  }

  setInventoryItemSettings(inventoryItem: InventoryItem): void {
    const containerId = inventoryItem.getContainerId();
    inventoryItem.isBin = this.settingService.getBinSetting(containerId);
    const supplierProductInclusionId = this.getSupplierProductInclusionId(inventoryItem);
    inventoryItem.witnessRequired = this.settingService.getWitnessRequiredSetting(containerId, supplierProductInclusionId);
    inventoryItem.verifyQOH = this.settingService.getVerifyQOHSetting(containerId, supplierProductInclusionId);
    inventoryItem.multiLotYN = this.settingService.getLotTrackingSetting(containerId);
  }

  setWitnessUserIdAndWitnessUserName(userClaims: UserClaims): void {
    this.dispenseItems.forEach(dispenseItem => {
      dispenseItem.inventoryItem.witnessUserId = Number(userClaims.userPartyId);
      dispenseItem.inventoryItem.witnessUserName = userClaims.name;
    });
  }

  loadBinGroups(): Observable<BinProductDetail[]> {
    return this.formularyService.getBinGroupDetails().pipe(
      tap((response: BinProductDetail[]) => {
        this.setBinGroups(response);
        return of(response);
      }));
  }

  getAssignedItemsByOrderMatching(orderDetails: FormularyOrderDetailLite[], inventoryList: InventoryItem[], filterByJcode: boolean,
    selectedPatientId: number): Observable<OrderWithMatchingDrugs[]> {
    const request: GetAssignedItemByOrderMatchingRequest = {
      orderDetails,
      filterByJcode,
      selectedPatientId,
    };
    return this.formularyService.getAssignedItemsByOrderMatching(request).pipe(
      tap((response: OrderWithMatchingDrugs[]) => {
        this.setOrdersWithMatchingItems(response, inventoryList, filterByJcode);
        return of(response);
      }));
  }

  autoConfigure(autoConfigRequest: AutoConfigureRequest): Observable<AutoConfigResponse> {
    return this.inventoryService.autoConfigure(autoConfigRequest).pipe(
      tap((response: AutoConfigResponse) => of(response)));
  }

  getJcodeExclusionList(): Observable<PickListItem[]> {
    return this.lookupService.getJcodeExclusionList().pipe(
      tap((response: PickListItem[]) => of(response)));
  }

  getBinGroups(): BinProductDetail[] {
    const key = CacheKeys.FormularyBinGroupList.toString();
    const data = this.cacheService.get(key);
    return data.getValue();
  }

  setBinGroups(binGroups: BinProductDetail[]): void {
    const key = CacheKeys.FormularyBinGroupList.toString();
    this.cacheService.set(key, binGroups);
  }

  checkBinGroupChartingSetting(inventoryItem: InventoryItem): InventoryItem {
    const data = this.cacheService.get(CacheKeys.FormularyBinGroupList.toString());
    if (!AbsgArrayHelper.isNil(data) && !AbsgArrayHelper.isNil(inventoryItem)) {
      const binProductDetails: BinProductDetail[] = data.getValue();
      const binProduct = AbsgArrayHelper.find(binProductDetails, y => y.supplierProductInclusionId === inventoryItem.meiParentId);
      if (!AbsgArrayHelper.isNil(binProduct)) {
        inventoryItem.useMegNotes = binProduct.useMegNotes;
        if (binProduct.useMegNotes) {
          const isNotesRequired = (binProduct.enableRequireNotes && !AbsgArrayHelper.isNil(binProduct.requireNotes));
          inventoryItem.requiredNote = new RequiredNote(binProduct.requireNotes, inventoryItem.requiredNote.note, isNotesRequired,
            binProduct.requireNotesOnLabel, binProduct.requireNotesOnReceipt);
        }
      }
    }
    return inventoryItem;
  }

  closeDispenseTransaction(): void {
    this.transactionApi.closeTransaction(TransactionStatus.SystemClosed).pipe(take(1))
      .subscribe(response => response,
        error => {
          this.handleError(error);
        });
  }

  addUpdateDispenseItem(dispenseItem: DispenseItem): void {
    const existingDispenseIndex = AbsgArrayHelper.findIndex(this.dispenseItems,
      item => item.inventoryItem.id === dispenseItem.inventoryItem.id,
    );
    const currentDispenseItem = AbsgArrayHelper.cloneDeep(dispenseItem);

    const patientInfo = this.getPatientInfoById(currentDispenseItem.inventoryItem.patientId);
    if (patientInfo) {
      currentDispenseItem.inventoryItem.patientInfo = patientInfo;
    }
    if (existingDispenseIndex > -1) {
      // Existing
      const invItem = this.dispenseItems[existingDispenseIndex].inventoryItem;
      currentDispenseItem.inventoryItem.autoPrintLabel = invItem.autoPrintLabel;
      currentDispenseItem.inventoryItem.autoPrintReceipt = invItem.autoPrintReceipt;
      currentDispenseItem.inventoryItem.requiredNote = invItem.requiredNote;
      currentDispenseItem.inventoryItem.useMegNotes = invItem.useMegNotes;
      currentDispenseItem.inventoryItem.isBin = invItem.isBin;
      currentDispenseItem.inventoryItem.multiLotYN = invItem.multiLotYN;

      // there might be more than one dispense items for same inventory id (multi lot scenario), they all need to be removed
      this.dispenseItems = this.dispenseItems.filter(di => di.inventoryItem.id !== currentDispenseItem.inventoryItem.id);
      this.dispenseItems.push(currentDispenseItem);

      const existingInventoryIndex = this.inventoryItems.getValue().findIndex(
        item => item.id === currentDispenseItem.inventoryItem.id,
      );
      if (existingInventoryIndex !== -1) {
        this.inventoryItems.getValue()[existingInventoryIndex].quantitySelected = currentDispenseItem.inventoryItem.quantitySelected;
      }
    } else {
      // New
      if (currentDispenseItem.inventoryItem.isBinned()) {
        const summaryNDCDispenseItemIdx = AbsgArrayHelper.findIndex(this.dispenseItems,
          ndcItem => (ndcItem.inventoryItem.getContainerId() === currentDispenseItem.inventoryItem.getContainerId())
            && (ndcItem.fromCabinetQuantity === 0 && ndcItem.fromUnderHoodQuantity === 0));
        if (summaryNDCDispenseItemIdx > -1) {
          if (this.historyTracker.has(dispenseItem.inventoryItem.getContainerId())) {
            // If this is a binned item, we would not send the item if it had already been used so bump the qty
            this.dispenseItems[summaryNDCDispenseItemIdx].inventoryItem.quantitySelected
              += currentDispenseItem.inventoryItem.quantitySelected;
            return;
          }
          this.dispenseItems.splice(summaryNDCDispenseItemIdx, 1);
        }
      }
      this.dispenseItems.push(currentDispenseItem);
    }
  }

  getDispenseItemByInventoryId(inventoryId: number): DispenseItem {
    return AbsgArrayHelper.find(this.dispenseItems, item => item.inventoryItem.id === inventoryId);
  }

  searchDiagnosis(query: string): Observable<ODataResult<DiagnosisCode>> {
    this.diagnosisCodes = this.diagnosisCodeService.searchDiagnosis(query);
    return this.diagnosisCodes;
  }

  getPatients(query = ''): Observable<Patient[]> {
    this.patients = this.patientService.getPatients(query);
    return this.patients;
  }

  getPatientDiagnosisById(patientId: number): Observable<DiagnosisCode[]> {
    return this.patientService.getPatientDiagnosisById(patientId).pipe(
      switchMap((diagnosisCodes) => this.diagnosisCodeService.getDiagnosesFromCodes(diagnosisCodes.map(x => x.code)).pipe(
        map(icdCodes => {
          this.selectedDiagnosisCodes = icdCodes.value;
          return this.selectedDiagnosisCodes;
        }),
      )));
  }

  // TODO: This method also exist in shared, need to remove it when we consolidate to extend the base service
  getPatientMedOrderDiagnosisCodes(): Observable<DiagnosisCode[]> {
    let diagnosisCodes: string[] = [];
    let svcOrders = this.medOrderDispenseDetailList.getValue();
    svcOrders = AbsgArrayHelper.filter(svcOrders, order => !order.detail.isRemovedOrder);
    if (!AbsgArrayHelper.isNil(svcOrders) && svcOrders.length > 0) {
      AbsgArrayHelper.forEach(svcOrders, (svcOrder: MedOrderDispenseDetail) => {
        if (svcOrder.detail.icd !== null) {
          const indx = svcOrder.detail.icd.indexOf(',');
          const codes = indx > 0 ? svcOrder.detail.icd.split(',') : svcOrder.detail.icd;
          if (!AbsgArrayHelper.isEmpty(codes)) {
            diagnosisCodes = diagnosisCodes.concat(codes);
          }
        }
      });
    }
    return this.getPatientDiagnosisCodes(diagnosisCodes);
  }

  private getPatientDiagnosisCodes(codes: string[]): Observable<DiagnosisCode[]> {
    return this.diagnosisCodeService.getDiagnosesFromCodes(codes).pipe(
      map(icdCodes => icdCodes.value));
  }

  getXrefs(): Observable<void> {
    const ids = this.getPositionContainerIds();
    this.settingService.stationContainerId = this.stationInfoService.station?.value?.id;
    return this.settingService.populateXrefs(ids).pipe(take(1));
  }

  removeDispenseItemByInventoryItemId(inventoryItemId: number, selectedMedOrderDetail?: MedOrderDispenseDetail): void {
    const existingDispenseIndex = this.dispenseItems.findIndex(
      item => item.inventoryItem.id === inventoryItemId,
    );
    if (existingDispenseIndex !== -1) {
      const dispenseItem = this.dispenseItems[existingDispenseIndex];
      const existingInvIndex = this.inventoryItems.getValue().findIndex(item => item.id === inventoryItemId);
      const medOrdersForDispenseItem = this.fetchMedOrdersByDispenseItem(dispenseItem);
      if (medOrdersForDispenseItem && medOrdersForDispenseItem.length > 1) {
        if (selectedMedOrderDetail) {
          // single drug multi MAR scenario. not to be removed from the list, update the quantity selected
          const medOrderToBeRemoved = medOrdersForDispenseItem.find(modi =>
            modi.getMedOrderId() === selectedMedOrderDetail.getMedOrderId());
          if (medOrderToBeRemoved) {
            const dispenseItemToBeRemoved = medOrderToBeRemoved.dispenseItems.find(di =>
              di.inventoryItem.supplierProductInclusionId === dispenseItem.inventoryItem.supplierProductInclusionId);
            if (dispenseItemToBeRemoved) {
              dispenseItem.inventoryItem.quantitySelected -= dispenseItemToBeRemoved.inventoryItem.quantitySelected;
              this.inventoryItems.getValue()[existingInvIndex].quantitySelected -= dispenseItemToBeRemoved.inventoryItem.quantitySelected;
            }
          }
        }
      } else {
        dispenseItem.inventoryItem.quantitySelected = 0;
        // SOmeone removed the location from the review screen, so make sure its not in the bin tracker
        this.removePositionFromHistoryTracker(dispenseItem.inventoryItem.getContainerId());
        this.dispenseItems.splice(existingDispenseIndex, 1);

        // remove previously selected items if originated from history
        if (dispenseItem.dispenseOrigin === DispenseTabs.HISTORY_TAB) {
          this.historyWorkflowSelectedItems = AbsgArrayHelper.filter(this.historyWorkflowSelectedItems, historySelectedItem =>
            historySelectedItem.supplierProductInclusionId !== dispenseItem.inventoryItem.supplierProductInclusionId);
        }
        if (existingInvIndex !== -1) {
          this.inventoryItems.getValue()[existingInvIndex].quantitySelected = 0;
        }
      }
    }
  }

  getCheapestItemDetails(containerId: number, productId: number): Observable<InventoryItem> {
    const inventoryItemsInBin = AbsgArrayHelper.cloneDeep(this.getBinContainerNDCs(containerId));
    return this.getAutoReorderItemIdForTransactionRequest(containerId,
      productId, inventoryItemsInBin)
      .pipe(take(1),
        tap(autoReorderInventoryItem => autoReorderInventoryItem));
  }

  saveDispenseItemsWithPointInTime(dispenseItems: DispenseItem[], dispenseSnapshotViewModel: DispenseSnapshotViewModel): Observable<Transaction> {
    let transactionRequest = this.createTransactionRequest(dispenseSnapshotViewModel);

    const details = AbsgArrayHelper.flatMap(dispenseItems, dispenseItem => this.createEventDetails(dispenseItem));
    transactionRequest.transactionEvent.itemDetails = details;
    if (this.medOrderDispenseItems && this.medOrderDispenseItems.length > 0) { // TODO: Add Med Order Info to the transactionRequest
      transactionRequest = this.addMedOrderInfo(transactionRequest);
    }
    if (!transactionRequest.dispenseSnapshot.dispenseDetailSnapshot ||
      transactionRequest.dispenseSnapshot.dispenseDetailSnapshot.length < 1) {
      this.handleMessage('not details to save');
      return of(null);
    }

    return this.dispenseApi.addDispenseItemSingleLotWithPointInTimeData(transactionRequest)
      .pipe(take(1), concatMap(transaction => {
        this.currentTransaction.next(transaction);
        return this.processTransactionDetails(transaction, dispenseItems);
      }));
  }

  saveDispenseItemsByLotWithPointInTimeData(dispenseItems: DispenseItem[], dispenseSnapshotViewModel: DispenseSnapshotViewModel): Observable<Transaction> {
    let transactionRequest = this.createTransactionRequest(dispenseSnapshotViewModel);
    // for multi lot or Bin item we will only have one dispense Item at a time
    if (dispenseItems[0].inventoryItem.isBin && !(dispenseItems[0].inventoryItem.isPracticeStatus())) {
      const containerId = dispenseItems[0].inventoryItem.getContainerId();
      const inventoryItemsInBin = this.getBinContainerNDCs(containerId);
      return this.getAutoReorderItemIdForTransactionRequest(containerId,
        dispenseItems[0].inventoryItem.productId, inventoryItemsInBin)
        .pipe(take(1),
          concatMap(autoReorderInventoryItem => {
            const details = AbsgArrayHelper.flatMap(dispenseItems, dispenseItem => {
              dispenseItem.cheapestItemId = autoReorderInventoryItem.itemId;
              dispenseItem.cheapestSpId = autoReorderInventoryItem.supplierProductInclusionId;
              return this.createEventDetails(dispenseItem);
            });
            transactionRequest.transactionEvent.itemDetails = details;
            if (this.medOrderDispenseItems && this.medOrderDispenseItems.length > 0) { // TODO: Add Med Order Info to the transactionRequest
              transactionRequest = this.addMedOrderInfo(transactionRequest);
            }
            return this.triggerDispenseMultiLotEvent(transactionRequest, dispenseItems).pipe(take(1));
          }));
    } else {
      const details = AbsgArrayHelper.flatMap(dispenseItems, dispenseItem => this.createEventDetails(dispenseItem));
      transactionRequest.transactionEvent.itemDetails = details;
      if (this.medOrderDispenseItems && this.medOrderDispenseItems.length > 0) { // TODO: Add Med Order Info to the transactionRequest
        transactionRequest = this.addMedOrderInfo(transactionRequest);
      }
      return this.triggerDispenseMultiLotEvent(transactionRequest, dispenseItems).pipe(take(1));
    }
  }

  processTransactionDetails(transaction: Transaction, dispenseItems: DispenseItem[]): Observable<Transaction> {
    AbsgArrayHelper.each(transaction.transactionDetails, detail => {
      AbsgArrayHelper.filter(dispenseItems, di =>
        di.inventoryItem.supplierProductInclusionId === detail.supplierProductInclusionId &&
        di.inventoryItem.storagePosition.id === detail.positionContainerId,
      )
        .forEach(di => {
          di.inventoryTransactionDetailId = detail.transactionDetailId;
          di.transactionId = transaction.transactionId;
          di.transactionDate = DateTimeHelper.convertUTCToLocalJSDate(transaction.transactionDate);
        });
    });
    return this.updateMedOrderPastDispenses().pipe(
      concatMap(() => this.saveDiagnosisCodesAsActivities(
        transaction,
        this.selectedDiagnosisCodes,
      ).pipe(take(1))),
    );
  }

  getActivityForLabelNotes(dispenseItems: DispenseItem[]): Activity[] {
    const labelNotesActivityType =
      this.commonDataService.getActivityTypeById(InventoryActivityTypes.DispenseLabelNotes);
    let currentTransactionId: number;
    if (this.currentTransaction.getValue()) {
      currentTransactionId = this.currentTransaction.getValue().transactionId;
    } else {
      throw new Error('Current Transaction is null or undefined');
    }
    const activities: Activity[] = AbsgArrayHelper.map(dispenseItems, dispenseItem => {
      const activity: Activity = {
        activityType: labelNotesActivityType,
        labelNote1: dispenseItem.labelLine1,
        labelNote2: dispenseItem.labelLine2,
        labelNote3: dispenseItem.labelLine3,
        transactionId: currentTransactionId,
        supplierProductInclusionId: dispenseItem.inventoryItem.supplierProductInclusionId,
        patientOwnedPatientId: dispenseItem.inventoryItem.patientId,
        patientOwnedItemTypeId: dispenseItem.inventoryItem.patientOwnedItemTypeId,
      };
      return activity;
    });
    return activities;
  }

  saveLabelNotes(dispenseItems: DispenseItem[]): void {
    const activities = this.getActivityForLabelNotes(dispenseItems);
    this.activityApi.addActivities(activities).subscribe(
      () => { },
      (error) => {
        this.handleError(error);
        this.nimAlertService.showErrorAlert('Error Saving Label Note');
      },
    );
  }

  saveLabelNotesForComprehensiveApiPrinting(dispenseItems: DispenseItem[]): Observable<Activity[]> {
    const activities = this.getActivityForLabelNotes(dispenseItems);
    return this.activityApi.addActivities(activities);
  }

  saveRequiredNotesAsActivities(dispenseItems: DispenseItem[]): Observable<Activity[]> {
    const requiredNotesActivityType =
      this.commonDataService.getActivityTypeById(InventoryActivityTypes.DispenseRequiredNotes);
    const activities: Activity[] = AbsgArrayHelper.map(dispenseItems, item => {
      const activity: Activity = {
        activityType: requiredNotesActivityType,
        requireNote: item.inventoryItem.requiredNote.label,
        requireNoteValue: item.inventoryItem.requiredNote.note,
        supplierProductInclusionId: item.inventoryItem.supplierProductInclusionId,
        patientOwnedPatientId: item.inventoryItem.patientId,
        patientOwnedItemTypeId: item.inventoryItem.patientOwnedItemTypeId,
        transactionId: item.transactionId,
      };
      return activity;
    });
    return this.activityApi.addActivities(activities);
  }

  setSelectedDispenseDisplay(item: DispenseDisplay): void {
    this.selectedDispenseDisplay = item;
  }

  getSelectedDispenseDisplay(): DispenseDisplay {
    return this.selectedDispenseDisplay;
  }

  setInventoryItems(inventoryItems: InventoryItem[]): void {
    this.inventoryItems.next(inventoryItems);
    this.dispenseState.products = inventoryItems;
  }

  countDispenseItems(): number {
    return this.dispenseItems.length;
  }

  clearDispenseData(): void {
    this.clearSelectedPatient();
    this.clearMedOrderPatient();
    this.clearSelectedAttendingPhysician();
    this.clearSelectedDiagnosisCodes();
    this.clearDispenseItems();
    this.clearMedOrderDispenseDetailsList();
    this.resetServiceDate();
    this.currentTransaction.next(null);
    this.sessionService.resetLogoutConfirmation();
    this.historyTracker.clear();
    this.clearDispenseWorkflowNavigatedTab();
    this.medOrderDispenseItems = [];
    this.clearHistoryWorkflowSelectedItems();
    this.dispenseState = new DispenseStateService();
  }

  clearDispenseItems(): void {
    this.dispenseItems = [];
    this.currentTransaction.next(null);
  }

  clearMedOrderDispenseDetailsList(): void {
    this.medOrderDispenseDetailList = new BehaviorSubject<MedOrderDispenseDetail[]>([]);
  }

  clearSelectedDiagnosisCodes(): void {
    this.selectedDiagnosisCodes = [];
    this.medOrderDiagnosisCodes = [];
  }

  clearSelectedAttendingPhysician(): void {
    this.selectedAttendingPhysician = null;
  }

  clearSelectedPatient(): void {
    this.selectedPatient = null;
  }

  clearMedOrderPatient(): void {
    this.medOrderPatient = null;
  }

  clearHistoryWorkflowSelectedItems(): void {
    this.historyWorkflowSelectedItems = [];
  }

  checkAndRemoveItemFromMedOrder(itemId: number): void {
    // single item multi MAR update - clear all the med orders linked with given dispense item
    if (this.medOrderDispenseItems && this.medOrderDispenseItems.length > 0) {
      const foundOrders = this.medOrderDispenseItems.filter(group => group.dispenseItems.some(item => item.getItemId() === itemId));
      if (foundOrders && foundOrders.length > 0) {
        foundOrders.forEach(foundOrder => this.clearSpecificMedOrder(foundOrder.medOrderDetail, itemId));
      }
    }
  }

  clearSpecificMedOrder(order: MedOrderDispenseDetail, itemId: number): void {
    if (this.medOrderDispenseItems && this.medOrderDispenseItems.length > 0) {
      AbsgArrayHelper.each(this.medOrderDispenseItems, group => {
        if (!order || !group || !group.dispenseItems) { return; }
        if (order.getMedOrderId() === group.medOrderDetail.getMedOrderId()) {
          const index = AbsgArrayHelper.findIndex(group.dispenseItems, item => item.getItemId() === itemId);
          if (index > -1) {
            group.dispenseItems.splice(index, 1);
          }
          this.updateMedOrderConfiguredAmount(group.getConfiguredAmount(), order.getMedOrderId());
        }
      });
      this.medOrderDispenseItems = AbsgArrayHelper.filter(this.medOrderDispenseItems, item => item.hasDispenseItemsAttached());
    }
  }

  clearFutureMedOrderOptions(): void {
    this.futureMedOrderOptions = null;
  }

  resetServiceDate(): void {
    this.serviceDate = new Date();
  }

  invalidateCacheIfNoDispense(): void {
    if (!this.dispenseItems || this.dispenseItems.length === 0) {
      this.isInventoryCached = false;
    }
  }

  clearCache(): void {
    this.isInventoryCached = false;
  }

  getWitnessSettingAtStation(): boolean {
    return this.settingService.getWitnessRequiredStationSetting();
  }

  getPrintAutoReceiptForDispenseItem(item: InventoryItem): boolean {
    const containerId = item.getContainerId();
    const supplierProductInclusionId = this.getSupplierProductInclusionId(item);
    return this.settingService.getReceiptForDispenseSetting(containerId, supplierProductInclusionId);
  }

  getAutoPrintLabelForDispenseItem(item: InventoryItem): boolean {
    const containerId = item.getContainerId();
    const supplierProductInclusionId = this.getSupplierProductInclusionId(item);
    return this.settingService.getAutoPrintLabelSetting(containerId, supplierProductInclusionId);
  }

  getBillingRequiredSetting(spiId: number): boolean {
    return this.settingService.getBillingRequiredSetting(spiId);
  }

  getSupplierProductInclusionIdList(items: InventoryItemSummary[]): Array<number> {
    this.cacheInventoryItems(items);

    const supplierProductInclusionIdList = new Array<number>();
    const inventoryItems = this.inventoryItems.getValue();
    inventoryItems.forEach(item =>
      supplierProductInclusionIdList.push(item.supplierProductInclusionId),
    );
    return supplierProductInclusionIdList;
  }

  getPositionContainerIds(): Array<number> {
    const inventoryItems = this.inventoryItems.getValue();
    const ids = AbsgArrayHelper.map(inventoryItems, invItem => {
      if (invItem.storagePosition) {
        return invItem.storagePosition.id;
      }
    });
    return ids;
  }

  searchInventoryItemsArray(query: string): Observable<InventoryItem[]> {
    const products = this.dispenseState.products;
    return of(this.searchInventoryItems(products, query));
  }

  processInventoryItems(products: ProductDetail[], emrMedDetails: PracticeEMRMedDetail[]): InventoryItem[] { // Map formulary Drug Id
    const searchResults = new Array<InventoryItem>();

    AbsgArrayHelper.each(this.inventoryItems.getValue(), inventoryItem => {
      const product = this.findProduct(inventoryItem.supplierProductInclusionId, products);
      const emrMedIdDetails = AbsgArrayHelper.filter(emrMedDetails,
        emrMedDetail => emrMedDetail.supplierProductInclusionId === inventoryItem.supplierProductInclusionId &&
          emrMedDetail.emrVendorId === EmrVendorEnum.OncoEMR);
      if (product) {
        // Formulary - Product Details
        FormularyConversionHelpers.convertUnitDisplayValues(product);
        inventoryItem.supplierProductId = product.supplierProductId;
        inventoryItem.dispenseUOMConversionFactor =
          product.dispenseUnitOfMeasureConversionFactor;
        inventoryItem.dispenseTypeId = product.dispenseTypeId;
        inventoryItem.dispenseTypeName = product.dispenseTypeName;
        inventoryItem.dispenseUnit = product.dispenseUnitOfMeasureDisplay;
        inventoryItem.genericName = product.genericName;
        inventoryItem.alternateName = product.alternateName;
        inventoryItem.inventoryUOMConversionFactor =
          product.inventoryUnitOfMeasureConversionFactor;
        inventoryItem.purchaseUOMConversionFactor = product.purchaseUnitOfMeasureConversionFactor;
        inventoryItem.inventoryUnit = product.inventoryUnitOfMeasureDisplay;
        inventoryItem.itemId = product.itemId;
        inventoryItem.name = product.displayName;
        inventoryItem.gnn = product.gnn;
        inventoryItem.gcnSequenceNumber = product.gcnSequenceNumber;
        inventoryItem.ndc = product.unitNdc;
        inventoryItem.packageNdc = product.packageNdc;
        inventoryItem.unitNdc = product.unitNdc;
        inventoryItem.tradeName = product.tradeName;
        inventoryItem.volumeUnit = product.volumeUnitOfMeasureDisplay;
        inventoryItem.volumeUOMConversionFactor =
          product.volumeUnitOfMeasureConversionFactor;
        inventoryItem.infusionNoteOnLabel = this.settingService.getInfusionNoteOnLabel(product.supplierProductInclusionId);
        inventoryItem.infusionNoteOnReceipt = this.settingService.getInfusionNoteOnReceipt(product.supplierProductInclusionId);
        inventoryItem.requiredNoteOnReceipt = this.settingService.getRequiredNoteOnReceipt(product.supplierProductInclusionId);
        inventoryItem.requiredNoteOnLabel = this.settingService.getRequiredNoteOnLabel(product.supplierProductInclusionId);
        inventoryItem.infuseNotes1 = product.infuseNotes1;
        inventoryItem.infuseNotes2 = product.infuseNotes2;
        inventoryItem.infuseNotes3 = product.infuseNotes3;
        inventoryItem.isPracticeFavorite = product.isPracticeFavorite;
        inventoryItem.refrigerated = product.refrigerated;
        inventoryItem.isDiscontinued = product.isDiscontinued;
        inventoryItem.formularyTypeId = product.formularyTypeId;
        inventoryItem.productGroupProducts = product.productGroupProducts;
        inventoryItem.bundleLabelName = product.bundleLabelName;
        inventoryItem.bundleLabelCode = product.bundleLabelCode;
        inventoryItem.productId = product.productId;
        inventoryItem.jCode = product.altJCode || (product.jCode || product.bundleCode);
        inventoryItem.altJCode = product.altJCode;
        inventoryItem.displayJCode = product.altJCode || product.jCode;
        inventoryItem.searchItemJCode = product.jCode;
        inventoryItem.requiredNote = new RequiredNote(product.requireNotes, null, null,
          inventoryItem.requiredNoteOnLabel, inventoryItem.requiredNoteOnReceipt);
        inventoryItem.billableUnitOfMeasureSize = product.billableUnitOfMeasureSize;
        inventoryItem.altBillableUnitOfMeasureSize = product.altBillableUnitOfMeasureSize;
        inventoryItem.meiParentId = product.meiParentId;
        inventoryItem.sapMaterialNumber = product.sapMaterialNumber;
        inventoryItem.isBuyIn = product.isBuyIn;
        inventoryItem.is340B = product.is340B;
        inventoryItem.isItem505b2 = product.isItem505b2;
        inventoryItem.manufactureId = product.manufacturerId;
        inventoryItem.emrMedIds = emrMedIdDetails.map(medId => medId.emrMedId);

        searchResults.push(inventoryItem);
      }
    });

    return searchResults;
  }

  /***
    * Returns the preferred Item as an Inventory Item if there is one
    * Or it will return the Inventory Item for the
    * cheapest item not found elsewhere on the station if no preferred
    */
  getAutoReorderItemIdForTransactionRequest(containerId: number,
    productId: number, inventoryItems: InventoryItem[]): Observable<InventoryItem> {
    // Has a preferred Item
    const preferredItemId = this.settingService.getPreferredItemIdSetting(containerId);
    if (preferredItemId) {
      const inventoryItem: InventoryItem = AbsgArrayHelper.find(inventoryItems, (item) => item.itemId === preferredItemId);
      if (!AbsgArrayHelper.isNil(inventoryItem)) {
        return of(inventoryItem);
      } else {
        return this.getAutoReorderItemDetail(containerId, productId, inventoryItems);
      }
    } else {
      return this.getAutoReorderItemDetail(containerId, productId, inventoryItems);
    }
  }

  setEmrConfigPreferences(dispenseItem: DispenseItem): void {
    if (dispenseItem.dispenseOrigin === DispenseTabs.MEDORDER_TAB) {
      this.emrService.getEmrConfig(false).pipe(take(1)).subscribe((emrConfig: EmrPracticeConfig) => {
        const dItem = AbsgArrayHelper.find(this.dispenseItems, di =>
          di.inventoryItem.supplierProductInclusionId === dispenseItem.inventoryItem.supplierProductInclusionId);
        if (dItem) {
          dItem.inventoryItem.instructionOnLabel = emrConfig.labelShowAdminInstructions;
          dItem.inventoryItem.routeOnLabel = emrConfig.labelShowRoute;
          dItem.inventoryItem.fluidOnLabel = emrConfig.labelShowFluid;
          dItem.inventoryItem.durationOnLabel = emrConfig.labelShowDuration;
        }
      });
    }
  }

  private updateMedOrderPastDispenses(): Observable<void> {
    const medOrderDetailIdList: number[] = this.medOrderDispenseItems.map(dsg => dsg.getMedOrderId());
    if (medOrderDetailIdList.length > 0) {
      return this.inventoryService.getDispenseInfoByMedOrderItems(medOrderDetailIdList).pipe(
        take(1),
        map((dispenseData: MedOrderTransactionActivityDetail[]) => {
          AbsgArrayHelper.forEach(this.medOrderDispenseItems, (modi: DispenseSelectedGroup) => {
            const pastDispenses = dispenseData.filter(dd => dd.medOrderDetailId === modi.getMedOrderId());
            modi.medOrderDetail.pastDispenses = pastDispenses;
          });
        }));
    } else {
      return of(null).pipe(map(() => { }));
    }
  }

  private getAutoReorderItemDetail(containerId: number,
    productId: number, inventoryItems: InventoryItem[],
  ): Observable<InventoryItem> {
    const allItems = this.stationInfoService.getAssignedInventoryItems().getValue();
    // Do not include any items assigned elsewhere in the station
    const excludeList = this.getSupplierProductInclusionIds(allItems.filter(i => i.positionContainerId !== containerId));
    const request = this.stationInfoService.generateGetCheapestEquivilantRequest(excludeList);
    return this.medicalEquivalentApi.getCheapestEquivilantForProduct(productId, request)
      .pipe(take(1),
        map(productDetail => {
          if ((productDetail) && !(AbsgArrayHelper.isNil(productDetail))) {
            const cheapestInventoryItem: InventoryItem = inventoryItems[0];
            cheapestInventoryItem.itemId = productDetail.itemId;
            cheapestInventoryItem.supplierProductInclusionId = productDetail.supplierProductInclusionId;
            cheapestInventoryItem.dispenseUOMConversionFactor = productDetail.dispenseUnitOfMeasureConversionFactor;
            cheapestInventoryItem.inventoryUOMConversionFactor = productDetail.inventoryUnitOfMeasureConversionFactor;
            cheapestInventoryItem.tradeName = productDetail.tradeName;
            cheapestInventoryItem.purchaseUOMConversionFactor = productDetail.purchaseUnitOfMeasureConversionFactor;
            return cheapestInventoryItem;
          } else {
            return inventoryItems[0];
          }
        }));
  }

  filterInventoryByMedOrderMatching(
    inventoryItems: InventoryItem[], medOrderDetail: MedOrderDispenseDetail, filterByJCode = true, filterByDisplayName = false,
  ): InventoryDisplay[] {
    let displayRows: InventoryDisplay[] = [];
    let gcnMatch = false;
    let jCodeMatch = false;
    let nameMatch = false;
    let scheduleMatch = false;
    let oncoEMRMedIdMatch = false;
    const medOrderDetailInfo = medOrderDetail.detail;
    const groupedItems =
      AbsgArrayHelper.find(this.medOrderDispenseItems, group => group.getMedOrderId() === medOrderDetail.detail.medOrderDetailId);
    const binInventoryItems = this.groupInventoryItemsForDisplay(inventoryItems);
    if (binInventoryItems.length > 0) {
      displayRows = binInventoryItems.filter((bii: InventoryDisplay) => {
        gcnMatch = false;
        jCodeMatch = false;
        nameMatch = false;
        scheduleMatch = false;
        oncoEMRMedIdMatch = false;
        const medOrderEmrMedId = medOrderDetailInfo.formularyDrugID?.toLowerCase();
        bii.defaultInventoryItem.isInsufficientQuantity =
          MedOrderDispenseHelper.isItemQtyInsufficientToFulfillOrder(bii, medOrderDetail, this.medOrderDispenseItems);
        if (medOrderDetailInfo.supplierProductInclusionId) {
          if (medOrderDetailInfo.patientOwnedItemTypeId) {
            if (bii.defaultInventoryItem.supplierProductInclusionId === medOrderDetailInfo.supplierProductInclusionId &&
              medOrderDetailInfo.patientOwnedPatientId === this.selectedPatient.id &&
              bii.defaultInventoryItem?.patientId === medOrderDetailInfo.patientOwnedPatientId) {
              scheduleMatch = true;
            }
          } else if (bii.defaultInventoryItem.supplierProductInclusionId === medOrderDetailInfo.supplierProductInclusionId &&
            AbsgArrayHelper.isNil(bii.defaultInventoryItem.patientId)) {
            scheduleMatch = true;
          } else {
            // handle the scenario where matching spiId was lost during bin grouping
            const matchingSpiInvItem = inventoryItems.find(i => i.supplierProductInclusionId === medOrderDetailInfo.supplierProductInclusionId);
            if (matchingSpiInvItem && matchingSpiInvItem.meiParentId) {
              // found matching item which was lost during bin grouping
              if (bii.defaultInventoryItem.meiParentId === matchingSpiInvItem.meiParentId && bii.isBin) {
                scheduleMatch = true;
              }
            }
          }
        }
        if (scheduleMatch === false) {
          if (filterByJCode) {
            if (medOrderEmrMedId && bii.defaultInventoryItem.emrMedIds?.some(emrMedId =>
              emrMedId.toLowerCase() === medOrderDetailInfo.formularyDrugID?.toLowerCase())) {
              oncoEMRMedIdMatch = true;
            } else {
              if (bii.defaultInventoryItem.gcnSequenceNumber !== '' && +medOrderDetailInfo?.gcn &&
                +bii.defaultInventoryItem.gcnSequenceNumber === +medOrderDetailInfo.gcn) {
                gcnMatch = true;
              }
              if (bii.defaultInventoryItem.jCode !== '' && medOrderDetailInfo?.jCode &&
                bii.defaultInventoryItem.jCode?.toLowerCase() === medOrderDetailInfo.jCode?.toLowerCase()) {
                jCodeMatch = true;
              }
              if (bii.binAltJCode || (bii?.binJCodes?.length > 0)) {
                jCodeMatch = false;
                // Stores alt JCode and JCode values for all assigned practice inventory items within the bin
                let invJCodes: string[] = [];
                const binInventoryItems = this.getBinContainerNDCs(bii.defaultInventoryItem.getContainerId());
                AbsgArrayHelper.map(binInventoryItems, invItem => {
                  if (!!invItem?.jCode) {
                    invJCodes.push(invItem.jCode.toLowerCase());
                  }
                  if (!!invItem?.altJCode) {
                    invJCodes.push(invItem.altJCode.toLowerCase());
                  }
                });
                invJCodes = AbsgArrayHelper.uniq(invJCodes);
                if (bii.binAltJCode) {
                  if (medOrderDetail?.detail?.jCode &&
                    bii.binAltJCode.toLowerCase() === medOrderDetailInfo.jCode.toLowerCase()) {
                    jCodeMatch = true;
                  }
                } else if (medOrderDetailInfo.jCode && invJCodes.includes(medOrderDetailInfo.jCode.toLowerCase())) {
                  // JCode match now considers both JCode and Alt JCode values specified for assigned practice inventory items within the bin
                  jCodeMatch = true;  //MedOrder with Blank J-Code should never match to anything in Nucleus
                }
              }
            }
          } else if (filterByDisplayName) {
            const drugNameStart = medOrderDetailInfo.componentName.substring(0,
              Math.min(4, medOrderDetailInfo.componentName.length)).toLowerCase();
            if ((bii.defaultInventoryItem.name || '').toLowerCase().startsWith(drugNameStart) ||
              (bii.defaultInventoryItem.genericName || '').toLowerCase().startsWith(drugNameStart) ||
              (bii.defaultInventoryItem.tradeName || '').toLowerCase().startsWith(drugNameStart) ||
              (bii.defaultInventoryItem.alternateName || '').toLowerCase().startsWith(drugNameStart) ||
              (bii.displayName || '').toLowerCase().startsWith(drugNameStart)) {
              nameMatch = true;
            }
          }
        }
        if (scheduleMatch || gcnMatch || jCodeMatch || nameMatch || oncoEMRMedIdMatch) {
          if (scheduleMatch) {
            bii.defaultInventoryItem.medOrderMatchLevel = MedOrderMatchEnum.Schedule;
          } else if (oncoEMRMedIdMatch) {
            bii.defaultInventoryItem.medOrderMatchLevel = MedOrderMatchEnum.OncoEMRMedIdMatch;
            if (bii.isBin) {
              const containerId = bii.defaultInventoryItem?.getContainerId();
              if (containerId) {
                AbsgArrayHelper.forEach(this.getContainerNDCsWithQtyOnHand(containerId), (item) => {
                  if (AbsgArrayHelper.some(item.emrMedIds, id => id?.toLowerCase() === medOrderEmrMedId)) {
                    item.medOrderMatchLevel = MedOrderMatchEnum.OncoEMRMedIdMatch;
                  }
                });
              }
            }
          } else if (bii.defaultInventoryItem?.patientId === this.selectedPatient?.id) {
            bii.defaultInventoryItem.medOrderMatchLevel = MedOrderMatchEnum.Patient;
          } else if (gcnMatch && jCodeMatch) {
            bii.defaultInventoryItem.medOrderMatchLevel = MedOrderMatchEnum.DualMatch;
          } else if (jCodeMatch) {
            bii.defaultInventoryItem.medOrderMatchLevel = MedOrderMatchEnum.JCode;
          } else if (gcnMatch) {
            bii.defaultInventoryItem.medOrderMatchLevel = MedOrderMatchEnum.Gcn;
          } else if (nameMatch) {
            bii.defaultInventoryItem.medOrderMatchLevel = MedOrderMatchEnum.Name;
          }
          bii.sortIndex = groupedItems
            ? AbsgArrayHelper.findIndex(groupedItems.dispenseItems, item => item.inventoryItem.id === bii.defaultInventoryItem.id)
            : 998;
          // Since we have to break order to have configured matching about configured search, we have to
          // invert part of the list and not the other. making these 998, 999 so we can pull them to the bottom
          // to switch to asc.
          if (bii.sortIndex === -1) { bii.sortIndex = 998; }
          if (bii.defaultInventoryItem.getSelectedQuantity() === 0) {
            bii.sortIndex = 999;
          }
          return bii;
        }
        if (groupedItems?.dispenseItems?.length > 0) {
          const inGroup = AbsgArrayHelper.findIndex(groupedItems.dispenseItems, item => item.inventoryItem.id === bii.defaultInventoryItem.id);
          if (inGroup > -1) {
            bii.defaultInventoryItem.medOrderMatchLevel = null;
            // Since we have to force configure search below configured matching, adding list length to make them
            // above matching in asc order.
            bii.sortIndex = inGroup + groupedItems.dispenseItems.length;
            return bii;
          }
        }
      });
    }
    const sortedRows = this.sortMedOrderDisplayRows(displayRows);
    return sortedRows;
  }

  sortMedOrderDisplayRows(dispenseItems: InventoryDisplay[]): InventoryDisplay[] {
    const order1 = AbsgArrayHelper.orderBy(dispenseItems,
      item => [
        item.defaultInventoryItem.isInsufficientQuantity,
        item.defaultInventoryItem.getSelectedQuantity(),
        item.defaultInventoryItem.medOrderMatchLevel,
        item.defaultInventoryItem.getDisplayName(),
      ], ['asc', 'desc', 'asc', 'asc']);
    return AbsgArrayHelper.sortBy(order1, item => [item.sortIndex]);
  }

  getInventoryItemsForDisplay(inventory: InventoryItem[], order: MedOrderDispenseDetail = null): InventoryDisplay[] {
    return this.groupInventoryItemsForDisplay(inventory, order);
  }

  getDispenseItemsForDisplay(inventory: DispenseItem[]): DispenseDisplay[] {
    const displayRows: DispenseDisplay[] = [];
    AbsgArrayHelper.each(inventory, item => {
      const existingRow = AbsgArrayHelper.find(displayRows, row => row.defaultInventoryItem.getContainerId() === item.inventoryItem.getContainerId());
      const binItems = this.getContainerNDCsWithQtyOnHand(item.inventoryItem.getContainerId());
      if (existingRow) {
        existingRow.totalDispenseQty += item.dispenseQuantity;
        existingRow.totalReturnQty += item.toCabinetQuantity;
        existingRow.totalWasteQty += item.wasteQuantity;

        // needed to display on the door page
        existingRow.skipTransaction = item.skipTransaction;
        existingRow.fromCabinetQuantity += item.fromCabinetQuantity;
        existingRow.fromUnderHoodQuantity += item.fromUnderHoodQuantity;
      } else {
        const displayRow = this.getInventoryDisplayInfo(item.inventoryItem) as DispenseDisplay;
        displayRow.totalDispenseQty = item.dispenseQuantity;
        displayRow.totalReturnQty = item.toCabinetQuantity;
        displayRow.totalWasteQty = item.wasteQuantity;
        // needed to display on the door page
        displayRow.isBin = item.inventoryItem.isBinned();
        const lots = AbsgArrayHelper.map(displayRow.defaultInventoryItem.inventoryItemProducts, i => i.lotNumber);
        displayRow.displayLotNumber = lots.length === 1 ? lots[0] : '';
        const lotExpirationDate = AbsgArrayHelper.map(displayRow.defaultInventoryItem.inventoryItemProducts, i => i.expirationDate);
        if (lotExpirationDate.length === 1) {
          const checkMarkerDate = ManagePositionHelper.formatDateOnly(lotExpirationDate[0]);
          displayRow.displayLotExpirationDate = checkMarkerDate === '' ? null : lotExpirationDate[0];
        } else {
          displayRow.displayLotExpirationDate = null;
        }
        displayRow.displaySingleNdcBin = displayRow.isBin ? (binItems.length === 1) : true;
        displayRow.displaySingleLot = (lots.length === 1);

        displayRow.skipTransaction = item.skipTransaction;
        displayRow.fromCabinetQuantity = item.fromCabinetQuantity;
        displayRow.fromUnderHoodQuantity = item.fromUnderHoodQuantity;
        displayRows.push(displayRow);
      }
    });
    return displayRows;
  }

  getQtyInCabinetForBin(containerId: number): number {
    return AbsgArrayHelper.reduce(
      AbsgArrayHelper.filter(this.dispenseState.allInventoryItems, i => i.getContainerId() === containerId),
      (sum, item: InventoryItem) => sum = CalculationHelper.add(item.quantityInCabinet, sum), 0);
  }

  getQtyUnderhoodForBin(containerId: number): number {
    return AbsgArrayHelper.reduce(
      AbsgArrayHelper.filter(this.dispenseState.allInventoryItems, i => i.getContainerId() === containerId),
      (sum, item: InventoryItem) => sum = CalculationHelper.add(item.quantityUnderHood, sum), 0);
  }

  getQtyOnHandForBin(containerId: number): number {
    return AbsgArrayHelper.reduce(
      AbsgArrayHelper.filter(this.dispenseState.allInventoryItems, i => i.getContainerId() === containerId),
      (sum, item: InventoryItem) => sum = CalculationHelper.add(item.getQuantityOnHand(), sum), 0);
  }

  setPositionRules(products: InventoryItem[]): void {
    AbsgArrayHelper.each((products), (item) => {
      this.setInventoryItemDispenseTypeIdFromPosition(item);
      this.setInventoryItemSettings(item);
    });
  }

  /**
   * This method gets label notes, required notes, infuse notes and physicians
   *
   * @param transactionIds unique dispense transaction IDs
   * @param inclusionIds unique supplier product inclusion IDs
   * @param physicianIds unique attending physician IDs
   */
  getPrintingData(transactionIds: number[], inclusionIds: number[], physicianIds: number[]): Observable<PrintingDataModel> {
    const activities: Activity[] = this.commonDataService.getActivityTypesByIds(transactionIds);
    let productDetails: ProductDetail[];
    let physicians: Physician[];
    let lots: ReturnWasteLot[];

    return forkJoin([
      this.formularyManagementService.searchFormularyItemsBasedOnInventory(inclusionIds, ''),
      this.physicianService.getPhysiciansById(physicianIds),
      this.inventoryService.getLotInfoByTransactions(transactionIds),
    ]).pipe(
      catchError((error: Error) => {
        this.handleError(error);
        return throwError(error);
      }),
      switchMap((results) => {
        productDetails = results[0];
        physicians = results[1];
        lots = results[2];

        return of(new PrintingDataModel(activities, productDetails, physicians, lots));
      }));
  }

  populateAutoReOrder(detail: EventDetailInfo, inventoryItem: InventoryItem): EventDetailInfo {
    const containerId = detail.positionContainerId;
    const supplierProductInclusionId = inventoryItem.isBinned() ? inventoryItem.meiParentId : detail.supplierProductInclusionId;
    const formularyTypeId = inventoryItem.formularyTypeId;
    const isPatientOwned = inventoryItem.isPatientOwnedStatus();

    detail.autoReorderEnabled =
      this.settingService.getAutoReOrderSetting(containerId, supplierProductInclusionId, formularyTypeId, isPatientOwned);
    detail.itemMinBefore = this.settingService.getMinSetting(containerId);
    detail.itemMinAfter = detail.itemMinBefore;
    detail.itemMaxBefore = this.settingService.getMaxSetting(containerId);
    detail.itemMaxAfter = detail.itemMaxBefore;

    return detail;
  }

  createEventDetails(dispenseItem: DispenseItem): EventDetailInfo[] {
    const eventDetails: EventDetailInfo[] = [];

    if (dispenseItem === null || dispenseItem.inventoryItem === null) {
      throw new Error('CreateEventDetails will Not accept a Null DispenseItem or InventoryItem');
    }

    // waste from underhood first.  If underhood amount is >= wasteQuantity waste all here.
    // If underhood amount < wasteQuantity, waste entire underhood amount and adjust remaining waste Quantity to be removed from the door
    let remainingQtyUnderhood = dispenseItem.inventoryItem.quantityUnderHood;
    let underHoodWasteQty = 0;
    let cabinetWasteQty = 0;
    if (remainingQtyUnderhood >= dispenseItem.wasteQuantity) {
      underHoodWasteQty = dispenseItem.wasteQuantity;
      remainingQtyUnderhood = CalculationHelper.subtract(remainingQtyUnderhood, underHoodWasteQty);
    } else {
      underHoodWasteQty = remainingQtyUnderhood;
      remainingQtyUnderhood = 0;
      cabinetWasteQty = CalculationHelper.subtract(dispenseItem.wasteQuantity, underHoodWasteQty);
    }

    // Determine breakdown of Dispensed Amount (from Underhood and from Cabinet)
    let amountDispensedFromUnderhood = 0;
    if (remainingQtyUnderhood <= dispenseItem.dispenseQuantity) {
      amountDispensedFromUnderhood = remainingQtyUnderhood;
    } else {
      amountDispensedFromUnderhood = dispenseItem.dispenseQuantity;
    }

    const amountDispensedFromCabinet = CalculationHelper.subtract(dispenseItem.dispenseQuantity, amountDispensedFromUnderhood);

    // Create EventDetailInfo when DispenseItem has quantity taken or returned to cabinet
    if (dispenseItem.fromCabinetQuantity > 0 || dispenseItem.toCabinetQuantity > 0 ||
      dispenseItem.fromUnderHoodQuantity > 0 || dispenseItem.toUnderHoodQuantity > 0) {
      let detailCabinet: EventDetailInfo;
      if (dispenseItem.inventoryItem.isBin) {
        detailCabinet = this.createEventDetailInfo(dispenseItem, dispenseItem.cheapestItemId, dispenseItem.cheapestSpId);
      } else {
        detailCabinet = this.createEventDetailInfo(dispenseItem);
      }

      detailCabinet.qtyOnHandAfter = CalculationHelper.add(
        CalculationHelper.subtract(
          CalculationHelper.add(dispenseItem.inventoryItem.quantityInCabinet, dispenseItem.toCabinetQuantity),
          dispenseItem.fromCabinetQuantity),
        CalculationHelper.subtract(
          CalculationHelper.add(dispenseItem.inventoryItem.quantityUnderHood, dispenseItem.toUnderHoodQuantity),
          dispenseItem.fromUnderHoodQuantity),
      );

      detailCabinet.qtyOnHandBefore =
        CalculationHelper.add(dispenseItem.inventoryItem.quantityInCabinet, dispenseItem.inventoryItem.quantityUnderHood);

      detailCabinet.positionContainerId = dispenseItem.inventoryItem.storagePosition.id;
      detailCabinet.reqQtyInDispenseUnits = CalculationHelper.add(amountDispensedFromCabinet, amountDispensedFromUnderhood);
      detailCabinet.reqQtyInDispenseUnitsFromCabinet = CalculationHelper.formatQuantity(dispenseItem.fromCabinetQuantity);
      detailCabinet.reqQtyInDispenseUnitsFromUnderHood = CalculationHelper.formatQuantity(dispenseItem.fromUnderHoodQuantity);
      detailCabinet.reqQtyInDispenseUnitsToCabinet = dispenseItem.toCabinetQuantity;
      detailCabinet.reqQtyInDispenseUnitsToUnderHood = dispenseItem.toUnderHoodQuantity;
      detailCabinet.reqWasteQtyInDispenseUnits = CalculationHelper.add(cabinetWasteQty, underHoodWasteQty);
      if (this.selectedAttendingPhysician) {
        detailCabinet.attendingPhysicianId = this.selectedAttendingPhysician.id;
      }
      detailCabinet.isLotTrackingOn = dispenseItem.inventoryItem.multiLotYN;
      detailCabinet.sapMaterialNumber = dispenseItem.inventoryItem.sapMaterialNumber;
      detailCabinet.isBin = dispenseItem.inventoryItem.isBin;
      detailCabinet.isBillingRequired = dispenseItem.inventoryItem.isBillingRequired;
      detailCabinet.isScanBypass = dispenseItem.isScanBypass;
      detailCabinet.scanBypassReason = dispenseItem.scanBypassReason;
      detailCabinet.scanBypassReasonListInclusionId = dispenseItem.scanBypassReasonListInclusionId;
      eventDetails.push(detailCabinet);
    }
    return eventDetails;
  }

  getPreferredItemForDispenseItem(item: InventoryItem): string {
    const containerId = item.getContainerId();
    return this.settingService.getPreferredItemIdSetting(containerId);
  }

  getMedOrderDispenseDetailsList(): MedOrderDispenseDetail[] {
    const list = this.medOrderDispenseDetailList.getValue();
    return list || [];
  }

  setMedOrderDispenseDetailsList(orders: MedOrderDispenseDetail[]): void {
    this.medOrderDispenseDetailList.next(orders);
  }

  getDispenseWorkflowNavigatedTab(): DispenseTabs {
    return this.dispenseWorkflowNavigatedTab;
  }

  setDispenseWorkflowNavigatedTab(navigatedTab: DispenseTabs): void {
    this.dispenseWorkflowNavigatedTab = navigatedTab;
  }

  clearDispenseWorkflowNavigatedTab(): void {
    this.dispenseWorkflowNavigatedTab = null;
  }

  findAlreadyConfiguredDispenseItem(supplierProductInclusionId: number, positionId: number): DispenseItem {
    return AbsgArrayHelper.find(this.dispenseItems,
      dispenseItem => dispenseItem.inventoryItem.supplierProductInclusionId === supplierProductInclusionId
        && dispenseItem.inventoryItem.storagePosition.id === positionId);
  }

  removeAllHistoryOriginItems(): void {
    AbsgArrayHelper.each(AbsgArrayHelper.filter(this.dispenseItems, item => item.dispenseOrigin === DispenseTabs.HISTORY_TAB), historyItem => {
      this.removeDispenseItemByInventoryItemId(historyItem.inventoryItem.id);
    });
  }

  removeHistoryWorkflowSelectedItem(supplierProductInclusionId: number): void {
    this.historyWorkflowSelectedItems = AbsgArrayHelper.filter(this.historyWorkflowSelectedItems,
      historySelectedItem => historySelectedItem.supplierProductInclusionId !== supplierProductInclusionId);
  }

  hasItemMultipleLots(item: InventoryItem): boolean {
    const cabinetLotWithQoh = item.inventoryItemProducts.filter(x => x.lotQuantity > 0 || x.lotQuantityUnderhood > 0).length;
    if (cabinetLotWithQoh > 1) {
      return true;
    }
    return false;
  }

  isSingleLotItemAfterWitness(witnessItem: WitnessItem, witnessItems: WitnessItem[]): boolean {
    return !witnessItems.some(wItem => wItem.positionContainerId === witnessItem.positionContainerId && wItem.userInputQtyInvUnitInCabinet > 0
      && wItem.supplierProductInclusionId !== witnessItem.supplierProductInclusionId);
  }

  getContainerNDCsWithQtyOnHand(containerId: number): InventoryItem[] {
    if (this.dispenseState.allInventoryItems) {
      return this.dispenseState.allInventoryItems
        .filter(x => x.getContainerId() === containerId && x.getQuantityOnHand() > 0);
    } else {
      return [];
    }
  }

  isOnlyItemInBinWithQOH(witnessItem: WitnessItem, witnessItems: WitnessItem[]): boolean {
    return witnessItem.userInputQtyInvUnitInCabinet > 0 && this.isSingleLotItemAfterWitness(witnessItem, witnessItems);
  }

  private getBinContainerNDCs(containerId: number): InventoryItem[] {
    if (this.dispenseState.allInventoryItems) {
      return this.dispenseState.allInventoryItems
        .filter(x => x.getContainerId() === containerId);
    } else {
      return [];
    }
  }

  adjustNullLotQty(lotNumberRequest?: CreateLotNumber): Observable<Array<InventoryItemProduct>> {
    return this.transactionApi.adjustNullLotQty(lotNumberRequest).pipe(
      tap(() => { }),
      catchError((response) => this.handleErrorResponse(response),
      ));
  }

  refreshInventoryCacheAsync(): Observable<void> {
    return this.stationInfoService.refreshInventoryCacheAsync();
  }

  /* #region Witness Door Methods */

  /**
   *
   * @param dispenseItems : All dispenseItem collection, which are part of current dispense workflow
   * @param extractedAccessPoint : Current extracted AccessPoint in progress
   */

  filterNWSLItems(dispenseItems: DispenseItem[], extractedAccessPoint: ExtractedAccessPoint): DispenseItem[] {
    // filter based on VerifyQOh = false and MultiYN = false and items return to door
    // Dispense the items filter from the method as soon as the door is opened.
    return dispenseItems.filter(item => this.determineNWSLDispenseItem(item, extractedAccessPoint));
  }

  /**
   *
   * @param dispenseItems : All dispenseItem collection, which are part of current dispense workflow
   * @param extractedAccessPoint : Current extracted AccessPoint in progress
   */
  filterVerifyQOHItems(dispenseItems: DispenseItem[], extractedAccessPoint: ExtractedAccessPoint): DispenseItem[] {
    // filter based on VerifyQOh = true
    return dispenseItems.filter(item => this.determineVerifyQohDispenseItems(item, extractedAccessPoint));
  }

  /**
   *
   * @param dispenseItems : All dispenseItem collection, which are part of current dispense workflow
   * @param extractedAccessPoint : Current extracted AccessPoint in progress
   */
  filterMultiLotOrBinItems(dispenseItems: DispenseItem[], extractedAccessPoint: ExtractedAccessPoint): DispenseItem[] {
    return dispenseItems.filter(item => this.determineMultiLotOrBinOrMultiMarDispenseItems(item, extractedAccessPoint));
  }

  /**
   *
   * @param dispenseItems : All dispenseItem collection, which are part of current dispense workflow
   * @param accessPointNumber : Current AccessPoint in progress. i.e Door or Fridge Number
   * @param targetLocationType : Type of Cabinet Door or Fridge or Virtual
   */

  filterNonVerifySingleLotItem(dispenseItems: DispenseItem[], accessPointNumber: number,
    targetLocationType: LocationType): DispenseItem[] {
    // filter based on VerifyQOh = false and MultiYN = false and items return to door
    // Dispense the items filter from the method as soon as the door is opened.
    return dispenseItems.filter(item => this.determineNonVerifySingleLotDispenseItem(item, accessPointNumber, targetLocationType));
  }

  /**
   *
   * @param dispenseItems : All dispenseItem collection, which are part of current dispense workflow
   * @param accessPointNumber : Current AccessPoint in progress. i.e Door or Fridge Number
   * @param targetLocationType : Type of Cabinet Door / Fridge / Virtual
   */
  filterVerifyQOHItem(dispenseItems: DispenseItem[], accessPointNumber: number,
    targetLocationType: LocationType): DispenseItem[] {
    // filter based on VerifyQOh = true
    return dispenseItems.filter(item => this.determineVerifyQohDispenseItem(item, accessPointNumber, targetLocationType));
  }

  /**
   *
   * @param witnessItems : All witness Items where UserInput were provided
   * @param dispenseItems : All dispenseItem collection, which are part of current dispense workflow
   */
  filterNonDiscrepancySingleLotItem(witnessItems: WitnessItem[], dispenseItems: DispenseItem[]): DispenseItem[] {
    const filteredWitnessItems = AbsgArrayHelper.filter(witnessItems, item => {
      if (!item.isMultiLotYN && (!item.isBinItem || (item.isBinItem && this.getContainerNDCsWithQtyOnHand(item.positionContainerId).length === 1
        && this.isSingleLotItemAfterWitness(item, witnessItems)))) {
        const totalQohBeforeInvUnit = CalculationHelper.convertToQuantity(
          CalculationHelper.add(item.qtyOnHandInUTH, item.qtyOnHandInCabinet),
          item.dispenseUnitOfMeasureConversionFactor, item.inventoryUnitOfMeasureConversionFactor);
        const totalQohUserEnteredInvUnit = CalculationHelper.add((item.userInputQtyInvUnitInCabinet),
          (item.userInputQtyInvUnitInUTH ? (item.userInputQtyInvUnitInUTH) : 0));

        if (totalQohBeforeInvUnit === totalQohUserEnteredInvUnit) {
          return true;
        }
      }
      return false;
    });
    const filteredDispenseItems: DispenseItem[] = new Array<DispenseItem>();
    AbsgArrayHelper.each(filteredWitnessItems, item => {
      const itemFiltered = dispenseItems.find(dispenseItem =>
        dispenseItem.inventoryItem.supplierProductInclusionId === item.supplierProductInclusionId
        && item.storagePosition?.id === dispenseItem.inventoryItem.getContainerId());
      if (!AbsgArrayHelper.isNil(itemFiltered)) {
        filteredDispenseItems.push(itemFiltered);
      }
    });
    return filteredDispenseItems;
  }

  /**
   *
   * @param witnessItems : should only include items that have discrepancy
   * @param dispenseItems : All dispenseItem collection, which are part of current dispense workflow
   */
  markDispenseItemAsSkipped(witnessItems: WitnessItem[], dispenseItems: DispenseItem[]): void {
    witnessItems.filter(item => item.skipTransaction).forEach(witnessItem => {
      const currentDispenseItem = dispenseItems
        .find(dItem => dItem.inventoryItem.supplierProductInclusionId === witnessItem.supplierProductInclusionId);
      if (!AbsgArrayHelper.isNil(currentDispenseItem)) {
        currentDispenseItem.skipTransaction = true;
      }
    });
  }

  /**
   *
   * @param witnessItems : should only include items that have discrepancy
   */
  filterWitnessItemsToContinue(witnessItems: WitnessItem[]): WitnessItem[] {
    // filter all the items which will proceed with dispense transaction after discrepancy changes.
    const filteredWitnessItems = witnessItems.filter(item => !item.skipTransaction);
    return filteredWitnessItems;
  }

  /**
   *
   * @param witnessItems : should only include items that have discrepancy
   */
  filterDiscrepancySingleLot(witnessItems: WitnessItem[]): WitnessItem[] {
    // filter all the items which will proceed with transaction and are single lot.
    const filteredWitnessItems = witnessItems.filter(item => !item.isMultiLotYN);
    return filteredWitnessItems;
  }

  /**
   *
   * @param dispenseItems : All dispenseItems for the Current Dispense Workflow
   * @param filteredDispenseItems : Current dispense Door Items to be display on dispense door html
   */
  updateFilteredDispenseItemsForDisplay(dispenseItems: DispenseItem[], filteredDispenseItems: DispenseItem[]): DispenseItem[] {
    const filterDispenseItems: DispenseItem[] = [];
    dispenseItems.filter(x => !x.skipTransaction).forEach(item => {
      const dispenseItem = AbsgArrayHelper.find(filteredDispenseItems,
        x => x.inventoryItem.supplierProductInclusionId === item.inventoryItem.supplierProductInclusionId &&
          x.inventoryItem.patientId === item.inventoryItem.patientId &&
          x.inventoryItem.patientOwnedItemTypeId === item.inventoryItem.patientOwnedItemTypeId);
      if (!AbsgArrayHelper.isNil(dispenseItem)) {
        filterDispenseItems.push(dispenseItem);
      }
    });
    return filterDispenseItems;
  }

  /**
   * This method will publish the status of all dispense requests i.e. single lot or multi-lot.
   * If requests are completed then true or false.
   */
  publishDispenseRequestStatus(): void {
    this.isSaveDispenseRequestsCompleted.next(this.dispenseRequestCounter === 0);
  }

  /**
   * This method should be called when sending a new dispense request to server
   * This method will add the request count by 1 to the stack
   */
  addNewSaveDispenseRequest(): void {
    this.dispenseRequestCounter++;
    this.publishDispenseRequestStatus();
  }

  /**
   * this method should be called when the response of a dispense request is completed
   * This method will remove the request count from the stack
   */
  removeCompletedDispenseRequest(): void {
    this.dispenseRequestCounter--;
    if (this.dispenseRequestCounter < 0) {
      throw new Error('More requests removed then added from the counter.');
    }
    this.publishDispenseRequestStatus();
  }

  reconfigureInventory(quantityChangeItems: WitnessItem[]): void {
    AbsgArrayHelper.each(quantityChangeItems, item => {
      const invItem = AbsgArrayHelper.find(this.dispenseState.allInventoryItems, i => i.itemId === item.itemId);
      if (invItem) {
        let lot = AbsgArrayHelper.find(invItem.inventoryItemProducts, p => p.lotNumber === item.lotNumber);
        if (AbsgArrayHelper.isNil(lot)) {
          lot = {
            lotNumber: null,
            expirationDate: null,
            lotQuantity: 0,
            inventoryItemId: item.inventoryItemId,
            supplierProductInclusionId: item.supplierProductInclusionId,
          };
          invItem.inventoryItemProducts.push(lot);
        }

        lot.lotQuantity = CalculationHelper.convertToQuantity(item.userInputQtyInvUnitInCabinet,
          invItem.inventoryUOMConversionFactor, invItem.dispenseUOMConversionFactor);
      }
    });
  }

  saveDispense(items: DispenseItem[]): Observable<boolean> {
    this.addNewSaveDispenseRequest();
    // prepare point in time object of dispense
    const practiceClinicDetail = this.dispenseCommonOperationsService.getPracticeClinicDetail();
    const dispenseSnapshot = this.dispensePointInTimeDataService.mapDispenseDataToDispensePointInTimeData(
      items,
      this.selectedAttendingPhysician,
      this.selectedPatient,
      practiceClinicDetail,
      this.medOrderDispenseItems,
    );

    return this.saveDispenseItemsByLotWithPointInTimeData(items, dispenseSnapshot)
      .pipe(
        filter(transaction => transaction !== null),
        take(1),
        map((transaction) => {
          // Make sure the transactionDetailId and transactionId are associated to the requiredNote.
          AbsgArrayHelper.each(items, item => {
            item.transactionId = transaction.transactionId;
            if (transaction && item && transaction.transactionDetails) {
              const findDetailResults = AbsgArrayHelper.filter(transaction.transactionDetails,
                td => td.supplierProductInclusionId === item.inventoryItem.supplierProductInclusionId);
              if (findDetailResults.length > 0) {
                item.inventoryTransactionDetailId = findDetailResults[0].transactionDetailId;
              }
            }
          });
          this.updateSummaryDispenseItem(items);
          this.removeCompletedDispenseRequest();
          return true;
        }),
        catchError(error => {
          this.handleError(error);
          this.nimAlertService.showErrorAlert('Transaction failed to saved');
          this.removeCompletedDispenseRequest();
          return of(false);
        }));
  }

  addProductToHistoryTracker(containerId: number, itemId: string): void {
    // This item from the position is now in the dispense, no need to do it again in the future
    if (!this.historyTracker.has(containerId)) {
      this.historyTracker.set(containerId, [itemId]);
    } else {
      this.historyTracker.get(containerId).push(itemId);
    }
  }

  isInventoryItemInHistoryTracker(item: InventoryItem): boolean {
    return AbsgArrayHelper.some(this.historyTracker.get(item.getContainerId()), itemId => item.itemId === itemId);
  }

  removePositionFromHistoryTracker(containerId: number): void {
    this.historyTracker.delete(containerId);
  }

  clearHistoryTracker(): void {
    this.historyTracker.clear();
  }

  setMedOrdersSelected(selectedMedOrder: MedOrderDispenseDetail): void {
    AbsgArrayHelper.each(this.getMedOrderDispenseDetailsList(), model => {
      if (!selectedMedOrder) {
        model.isSelected = false;
      } else if (model.detail && selectedMedOrder.detail &&
        model.detail.medOrderDetailId === selectedMedOrder.detail.medOrderDetailId) {
        model.isSelected = selectedMedOrder.isSelected;
      } else {
        model.isSelected = false;
      }
    });
  }

  updateMedOrderDispenseItemsForBin(gridDispenseItems: DispenseItem[]): void {
    if (gridDispenseItems[0].inventoryItem.isBin && this.medOrderDispenseItems && this.medOrderDispenseItems.length > 0) {
      const medOrderDetailForItems = this.medOrderDispenseItems.filter(moItem => moItem.dispenseItems.some(mdi =>
        gridDispenseItems.find(di => di.inventoryItem.id === mdi.inventoryItem.id)));
      // update this.medOrderDispenseItems with the original collection only for the items available in grid dispense items
      AbsgArrayHelper.forEach(medOrderDetailForItems, modfi => {
        const itemsToRetain = AbsgArrayHelper.cloneDeep(AbsgArrayHelper.filter(modfi.dispenseItems, item =>
          !AbsgArrayHelper.map(gridDispenseItems, x => x.inventoryItem.id).includes(item.inventoryItem.id)));
        const originalMedOrderDetail = AbsgArrayHelper.find(this.originalMedOrderDispenseItems, omod =>
          omod.getMedOrderId() === modfi.getMedOrderId());
        const originalItems = AbsgArrayHelper.cloneDeep(AbsgArrayHelper.filter(originalMedOrderDetail.dispenseItems, item =>
          AbsgArrayHelper.map(gridDispenseItems, x => x.inventoryItem.id).includes(item.inventoryItem.id)));
        modfi.dispenseItems = [];
        modfi.dispenseItems = AbsgArrayHelper.concat(modfi.dispenseItems, itemsToRetain, originalItems);
      });

      if (medOrderDetailForItems.length === 1) {
        // remove the bin item from the collection
        const index = medOrderDetailForItems[0].dispenseItems.findIndex(mdi =>
          gridDispenseItems.find(di => di.inventoryItem.id === mdi.inventoryItem.id));
        if (index > -1) {
          medOrderDetailForItems[0].dispenseItems.splice(index, 1);
        }
        gridDispenseItems.forEach(di => {
          if (di.fromCabinetQuantity > 0 || di.fromUnderHoodQuantity > 0) {
            medOrderDetailForItems[0].dispenseItems.push(di);
          }
        });
      } else if (medOrderDetailForItems.length > 1) {
        //  multi MAR scenario
        let index = 0;
        let amountConsmedOfCurrentItem = 0;
        medOrderDetailForItems.forEach(modi => {
          let remainingQtySelected = 0;
          const dispenseItemIndex = modi.dispenseItems.findIndex(di => gridDispenseItems.map(gdi =>
            gdi.inventoryItem.id).includes(di.inventoryItem.id));
          const matchingDispenseItem = modi.dispenseItems[dispenseItemIndex];
          const originalQuantitySelected = matchingDispenseItem.inventoryItem.quantitySelected;
          remainingQtySelected = originalQuantitySelected;
          modi.dispenseItems.splice(dispenseItemIndex, 1);
          for (let i = index; i < gridDispenseItems.length; i++) {
            if (remainingQtySelected > 0) {
              const di = AbsgArrayHelper.cloneDeep(gridDispenseItems[i]);
              if (di.fromCabinetQuantity > 0 || di.fromUnderHoodQuantity > 0) {
                const amountAvailableToSelect = CalculationHelper.subtract(di.inventoryItem.quantitySelected, amountConsmedOfCurrentItem);
                const quantitySelected = Math.min(remainingQtySelected, amountAvailableToSelect);
                // keep waste with last part of the grid dispense item
                di.wasteQuantity = amountAvailableToSelect > quantitySelected ? 0 : di.wasteQuantity;
                di.fromCabinetQuantity = amountAvailableToSelect > quantitySelected ? 0 : di.fromCabinetQuantity;
                di.inventoryItem.quantitySelected = quantitySelected;
                if (amountAvailableToSelect > 0) {
                  modi.dispenseItems.push(di);
                }
                remainingQtySelected = CalculationHelper.subtract(remainingQtySelected, di.inventoryItem.quantitySelected);
                index = i;
                amountConsmedOfCurrentItem = remainingQtySelected === 0 ? quantitySelected : 0;
              }
            } else {
              break;
            }
          }
        });
      }
    }
  }

  addMedOrderInfo(transactionRequest: TransactionRequest): TransactionRequest {
    transactionRequest.medOrderDispenseItems = [];
    // distribute the dispensed qty lot wise while sending to inv api
    AbsgArrayHelper.forEach(this.medOrderDispenseItems, t => {
      const med: MedOrderDispenseItem = {
        medOrderDetail: {
          pastDispenses: t.medOrderDetail.pastDispenses,
          detail: this.BuildMedOrderDetail(t.medOrderDetail.detail),
          binGroupInventoryItems: t.medOrderDetail.matchingInventoryItems.map(a => this.BuildBinGroupInventoryItems(a)),
        },
        inventoryItems: [], // to be filled in this.distributeNdcAndLots
      };
      transactionRequest.medOrderDispenseItems.push(med);
    });
    this.distributeNdcAndLots(transactionRequest);
    if (this.futureMedOrderOptions) {
      transactionRequest.medOrderClinicId = this.futureMedOrderOptions.clinicId;
    }

    const dispenseToBeSavedData = AbsgArrayHelper.cloneDeep(transactionRequest);
    dispenseToBeSavedData.dispenseSnapshot = null;
    this.handleMessage(`dispense data to be saved : ${JSON.stringify(dispenseToBeSavedData)}`);

    return transactionRequest;
  }

  distributeNdcAndLots(transactionReq: TransactionRequest): void {
    const invItemIds = AbsgArrayHelper.uniq(transactionReq.transactionEvent.itemDetails.map(id => id.inventoryItemId));
    for (const invItemId of invItemIds) {
      const itemDetails = AbsgArrayHelper.filter(transactionReq.transactionEvent.itemDetails, id => id.inventoryItemId === invItemId);
      const identifierDetails: IdentifierDetail[] = [];
      AbsgArrayHelper.forEach(itemDetails, id => {
        const identifierDetail = {
          invIdentifierId: id.invItemProductIdentifierId,
          isExhausted: false,
          isWasteAdded: false,
          lotnumber: id.lotNumber,
          remainingDispenseAmount: id.reqQtyInDispenseUnits,
          wasteAmount: id.reqWasteQtyInDispenseUnits,
        } as IdentifierDetail;
        identifierDetails.push(identifierDetail);
      });
      for (const txnModi of transactionReq.medOrderDispenseItems) {
        const modi = AbsgArrayHelper.find(this.medOrderDispenseItems, md => md.getMedOrderId() === txnModi.medOrderDetail.detail.medOrderDetailId);
        if (modi) {
          const matchingMedDispenseItems = AbsgArrayHelper.filter(modi.dispenseItems, mdi => mdi.inventoryItem.id === invItemId);
          if (matchingMedDispenseItems.length > 0) {
            for (const mdi of matchingMedDispenseItems) {
              let remainingMedAmount = mdi.inventoryItem.quantitySelected;
              for (const identifierDetail of AbsgArrayHelper.filter(identifierDetails, id => id.isExhausted === false)) {
                if (remainingMedAmount > 0) {
                  const invItem = AbsgArrayHelper.cloneDeep(mdi.inventoryItem);
                  invItem.quantitySelected = Math.min(remainingMedAmount, identifierDetail.remainingDispenseAmount);
                  txnModi.inventoryItems.push(this.BuildApiInventoryItem(invItem, identifierDetail.invIdentifierId,
                    identifierDetail.isWasteAdded ? 0 : identifierDetail.wasteAmount));
                  remainingMedAmount = CalculationHelper.subtract(remainingMedAmount, identifierDetail.remainingDispenseAmount);
                  if (remainingMedAmount >= 0) {
                    identifierDetail.isExhausted = true;
                    identifierDetail.isWasteAdded = true;
                    identifierDetail.remainingDispenseAmount = 0;
                  } else {
                    identifierDetail.isExhausted = false;
                    identifierDetail.isWasteAdded = true;
                    identifierDetail.remainingDispenseAmount = CalculationHelper.subtract(identifierDetail.remainingDispenseAmount, invItem.quantitySelected);
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  configureMedOrder(dispenseItem: DispenseItem, selectedMedOrderDetail?: MedOrderDispenseDetail): void {
    if (!dispenseItem) { return; }
    let index = -1;
    const id = selectedMedOrderDetail ? selectedMedOrderDetail.detail.medOrderDetailId : -1;
    if (id > -1) {
      index = AbsgArrayHelper.findIndex(this.medOrderDispenseItems, item => item.getMedOrderId() === id);
    }
    if (index < 0) {
      if (!selectedMedOrderDetail) { return; }
      const group = new DispenseSelectedGroup(selectedMedOrderDetail, [AbsgArrayHelper.cloneDeep(dispenseItem)]);
      index = this.medOrderDispenseItems.push(group) - 1;
    } else {
      const identicalDispenseId = AbsgArrayHelper.findIndex(this.medOrderDispenseItems[index].dispenseItems,
        item => item.getItemId() === dispenseItem.getItemId());
      if (identicalDispenseId < 0) {
        this.medOrderDispenseItems[index].dispenseItems.push(AbsgArrayHelper.cloneDeep(dispenseItem));
      } else {
        this.medOrderDispenseItems[index].dispenseItems[identicalDispenseId] = AbsgArrayHelper.cloneDeep(dispenseItem);
      }
    }
    const newList = this.getMedOrderDispenseDetailsList();
    AbsgArrayHelper.each(newList, item => {
      if (item.detail.medOrderDetailId === id) {
        item.configuredAmount = this.sumSelectedAmount(this.medOrderDispenseItems[index].dispenseItems);
        item.configuredUnit = dispenseItem.inventoryItem.dispenseUnit;
        if (dispenseItem.inventoryDisplay) {
          item.matchingInventoryItems.push(dispenseItem.inventoryDisplay);
        }
        return;
      }
    });
    this.setMedOrderDispenseDetailsList(newList);
  }

  updateMedOrderConfiguredAmount(newAmount: number, orderId: number): void {
    const orders = this.getMedOrderDispenseDetailsList();
    AbsgArrayHelper.each(orders, order => {
      if (order.getMedOrderId() === orderId) {
        order.configuredAmount = newAmount;
        return false;
      }
    });
    // Trigger events to update orders.
    this.setMedOrderDispenseDetailsList(orders);
  }

  fetchMedOrdersByDispenseItem(dispenseItem: DispenseItem): DispenseSelectedGroup[] {
    return this.medOrderDispenseItems.filter((group: DispenseSelectedGroup) => group.dispenseItems.some((di: DispenseItem) =>
      di.inventoryItem.supplierProductInclusionId === dispenseItem.inventoryItem.supplierProductInclusionId &&
      di.inventoryItem.patientId === dispenseItem.inventoryItem.patientId &&
      di.inventoryItem.patientOwnedItemTypeId === dispenseItem.inventoryItem.patientOwnedItemTypeId));
  }

  // TODO: remove it once single item multi MAr toggle flag is removed
  checkIfItemExistsOnAnotherMedOrder(itemId: number, orderId: number): boolean {
    const index = AbsgArrayHelper.findIndex(this.medOrderDispenseItems, group => {
      if (group.getMedOrderId() !== orderId) {
        const itemIndex = AbsgArrayHelper.findIndex(group.dispenseItems, item => item.getItemId() === itemId);
        return itemIndex > -1;
      }
    });
    return index > -1;
  }

  sumSelectedAmount(dispenseItems: DispenseItem[]): number {
    let finalAmount = 0;
    AbsgArrayHelper.each(dispenseItems, item => {
      finalAmount = CalculationHelper.add(finalAmount, item.inventoryItem.quantitySelected);
    });
    return finalAmount;
  }

  mapOrdersToFormularyOrderDetailLite(): FormularyOrderDetailLite[] {
    const orders: FormularyOrderDetailLite[] = [];
    let svcOrders = this.medOrderDispenseDetailList.getValue();
    svcOrders = AbsgArrayHelper.filter(svcOrders, order => !order.detail.isRemovedOrder);
    if (!AbsgArrayHelper.isNil(svcOrders) && svcOrders.length > 0) {
      AbsgArrayHelper.forEach(svcOrders, (svcOrder: MedOrderDispenseDetail) => {
        const order: FormularyOrderDetailLite = {
          orderDetailId: svcOrder.getMedOrderId(),
          emrOrderId: svcOrder.detail.emrOrderId,
          doseAmount: svcOrder.getMedOrderDoseAmount(),
          dispenseDoseUnit: svcOrder.getMedOrderDoseUnit(),
          supplierProductInclusionId: svcOrder.detail.supplierProductInclusionId,
          patientId: svcOrder.detail.patientOwnedPatientId,
          patientOwnedItemTypeId: svcOrder.detail.patientOwnedItemTypeId,
          componentName: svcOrder.detail.componentName,
          gcn: svcOrder.detail.gcn,
          jcode: svcOrder.detail.jCode,
          oncoEMRFormularyMedId: svcOrder.detail.formularyDrugID,
        };
        orders.push(order);
      });
    }
    return orders;
  }

  mapAutoConfigureRequest(ordersWithmatchingItems: OrderWithMatchingDrugs[], excludedJcodeList: PickListItem[], inventoryList: InventoryItem[]): AutoConfigureRequest {
    AbsgArrayHelper.forEach(ordersWithmatchingItems, omi => {
      AbsgArrayHelper.forEach(omi.matchingDrugs, md => {
        const matchingInventory = AbsgArrayHelper.find(inventoryList, inv => inv.getContainerId() === md.positionContainerId && inv.supplierProductInclusionId === md.supplierProductInclusionId);
        if (matchingInventory) {
          md.inventoryItemId = matchingInventory.id;
        }
      });
    });
    return {
      jcodeExclusionList: excludedJcodeList.map(item => item.text),
      needQohFromInventory: true,
      orderListWithMatchingDrugs: ordersWithmatchingItems,
    } as AutoConfigureRequest;
  }

  setOrdersWithMatchingItems(ordersWithMatchingDrugs: OrderWithMatchingDrugs[], inventoryList: InventoryItem[], filterByJCode: boolean): void {
    const orderDetails = this.getMedOrderDispenseDetailsList();
    AbsgArrayHelper.forEach(orderDetails, (orderDetail: MedOrderDispenseDetail) => {
      const incomingOrder = AbsgArrayHelper.find(ordersWithMatchingDrugs, omd => omd.medOrderDetail.orderDetailId === orderDetail.getMedOrderId());
      if (incomingOrder && !AbsgArrayHelper.isNil(incomingOrder.matchingDrugs) && incomingOrder.matchingDrugs.length > 0) {
        const matchingInventory = AbsgArrayHelper.filter(inventoryList, inv =>
          AbsgArrayHelper.some(incomingOrder.matchingDrugs, md => md.positionContainerId === inv.getContainerId()));
        if (!AbsgArrayHelper.isNil(matchingInventory) && matchingInventory.length > 0) {
          const groupedInventoryItems = this.filterInventoryByMedOrderMatching(matchingInventory, orderDetail, filterByJCode, !filterByJCode);
          if (!AbsgArrayHelper.isNil(groupedInventoryItems) && groupedInventoryItems.length > 0) {
            orderDetail.matchingInventoryItems = groupedInventoryItems;
          }
        }
      }
    });
    this.setMedOrderDispenseDetailsList(orderDetails);
  }

  processAutoConfigResponse(response: AutoConfigResponse): void {
    if (response?.medOrderDetailConfigs?.length > 0) {
      const orderDetails = this.getMedOrderDispenseDetailsList();
      AbsgArrayHelper.forEach(response.medOrderDetailConfigs, (autoConfig: MedOrderDetailConfig) => {
        if (autoConfig?.bestConfig?.drugs?.length > 0) {
          // orderDetail has found a configuration.Find the medorder, configure it
          const orderDetail = AbsgArrayHelper.find(orderDetails, detail => detail.getMedOrderId() === autoConfig.medOrderDetail.orderDetailId);
          if (orderDetail) {
            // ideally the incoming drugs shud have unique positionId as the grouping is handled in inv api
            const uniqPositionIds = AbsgArrayHelper.uniq(autoConfig.bestConfig.drugs.map(d => d.positionContainerId || 0));
            AbsgArrayHelper.forEach(uniqPositionIds, positionId => {
              const invDisplay = AbsgArrayHelper.find(orderDetail.matchingInventoryItems, mii => mii.defaultInventoryItem.getContainerId() === positionId);
              if (invDisplay) {
                // found the matching item. now see if incoming drugs has single or multiple items for this position
                const matchingDrugs = AbsgArrayHelper.filter(autoConfig.bestConfig.drugs, drug => drug.positionContainerId === positionId);
                if (matchingDrugs.length > 0) {
                  const configAmount = AbsgArrayHelper.sum(matchingDrugs.map(drug => CalculationHelper.add(drug.amountConfiguredFromFraction, drug.amountConfiguredFromFullContainer)));
                  invDisplay.defaultInventoryItem.quantitySelected = configAmount;
                  invDisplay.defaultInventoryItem.instructionOnLabel = this.emrPracticeConfigDetail?.labelShowAdminInstructions;
                  invDisplay.defaultInventoryItem.routeOnLabel = this.emrPracticeConfigDetail?.labelShowRoute;
                  invDisplay.defaultInventoryItem.fluidOnLabel = this.emrPracticeConfigDetail?.labelShowFluid;
                  invDisplay.defaultInventoryItem.durationOnLabel = this.emrPracticeConfigDetail?.labelShowDuration;
                  this.configureItem(invDisplay, orderDetail, configAmount);
                }
              }
            });
          }
        }
      });
    }
  }

  private groupInventoryItemsForDisplay(inventory: InventoryItem[], medOrderDetail: MedOrderDispenseDetail = null,
  ): InventoryDisplay[] {
    const displayRows: InventoryDisplay[] = [];
    AbsgArrayHelper.each(inventory, item => {
      let rowIndex = -1;
      const existingRow = AbsgArrayHelper.find(displayRows, (row, index) => {
        const result = row.defaultInventoryItem.getContainerId() === item.getContainerId();
        if (result) {
          rowIndex = index;
          return result;
        }
      });
      if (!existingRow) {
        const displayRow = this.getInventoryDisplayInfo(item) as InventoryDisplay;
        displayRow.totalQuantityInBin = this.getQtyOnHandForBin(displayRow.defaultInventoryItem.getContainerId());
        displayRow.defaultInventoryItem.isInsufficientQuantity =
          MedOrderDispenseHelper.isItemQtyInsufficientToFulfillOrder(
            displayRow, medOrderDetail, this.medOrderDispenseItems);

        // For bin display row, use first container item that has quantity; otherwise pick default to display "out of stock" row
        const positionNDCs = this.getContainerNDCsWithQtyOnHand(item.getContainerId());
        if (positionNDCs.length > 0) {
          for (const ndc of positionNDCs) {
            if (item.itemId === ndc.itemId) {
              displayRows.push(displayRow);
            }
          }
        } else {
          displayRows.push(displayRow);
        }
      } else {
        if (item.isBin) {
          // TODO: binJCodes are already set for a bin item from the above function getInventoryDisplayInfo. Below line will just add duplicate
          displayRows[rowIndex].binJCodes.push(item.jCode?.toLowerCase());
        }
      }
    });
    if (medOrderDetail) {
      return AbsgArrayHelper.orderBy(displayRows, [row => row.defaultInventoryItem.patientId, row => row.displayName.toLowerCase()]);
    } else {
      return this.getInventoryRows(displayRows);
    }
  }

  private getInventoryRows(displayRows: InventoryDisplay[]) {
    const selectedInventoryRows = this.getInventoryForSelectedPatientOwnedItems(displayRows);
    const otherPatientInventoryRows = this.getInventoryforOtherPatientsOwnedItems(displayRows);
    return AbsgArrayHelper.orderBy(displayRows, [
      row => row.displayName,
      row => {
        if (row.defaultInventoryItem?.patientId === this.selectedPatient?.id) {
          return row.defaultInventoryItem?.patientOwnedItemTypeFullName;
        }
      },
      row => {
        if (AbsgArrayHelper.isNil(row.defaultInventoryItem?.patientId)) {
          if (displayRows.find(x => x.defaultInventoryItem.getDisplayName().toLowerCase() === selectedInventoryRows[0]?.defaultInventoryItem?.getDisplayName().toLowerCase())) {
            return row.defaultInventoryItem?.getDisplayName();
          }
        }
      },
      row => {
        if (AbsgArrayHelper.isNil(row.defaultInventoryItem?.patientId)) {
          if (displayRows.find(x => x.defaultInventoryItem.getDisplayName().toLowerCase() === otherPatientInventoryRows[0]?.defaultInventoryItem?.getDisplayName().toLowerCase())) {
            return row.defaultInventoryItem?.getDisplayName();
          }
        }
      },
      row => {
        if (!AbsgArrayHelper.isNil(row.defaultInventoryItem?.patientId) && row.defaultInventoryItem?.patientId !== this.selectedPatient.id) {
          return [row.defaultInventoryItem?.patientInfo?.lastName,
          row.defaultInventoryItem?.patientInfo?.firstName,
          row.defaultInventoryItem?.patientOwnedItemTypeFullName];
        }
      },
    ]);
  }

  private getInventoryForSelectedPatientOwnedItems(displayRows: InventoryDisplay[]): InventoryDisplay[] {
    return AbsgArrayHelper.filter(displayRows,
      row => !AbsgArrayHelper.isNil(this.selectedPatient?.id) && (row.defaultInventoryItem.patientId === this.selectedPatient.id),
    );
  }

  private getInventoryforOtherPatientsOwnedItems(displayRows: InventoryDisplay[]): InventoryDisplay[] {
    return AbsgArrayHelper.filter(displayRows,
      row => !AbsgArrayHelper.isNil(row.defaultInventoryItem.patientId) &&
        (row.defaultInventoryItem.patientId !== this.selectedPatient?.id),
    );
  }

  private processMedOrderDetailData(medOrder: MedOrder, dispenseData: MedOrderTransactionActivityDetail[]): MedOrderDispenseDetail[] {
    const medOrderDispenseDetailList: MedOrderDispenseDetail[] = [];
    medOrder.details.forEach((detail: MedOrderDetail) => {
      let medOrderDispenseDetail = new MedOrderDispenseDetail();
      medOrderDispenseDetail.detail = detail;
      medOrderDispenseDetail.routeToHide = this.emrPracticeConfigDetail.routesToHide ?? '';
      medOrderDispenseDetail.configuredAmount = 0;
      const dispenseDataForMedOrder = dispenseData.filter(dd => dd.medOrderDetailId === detail.medOrderDetailId);
      if (dispenseDataForMedOrder && dispenseDataForMedOrder.length > 0) {
        medOrderDispenseDetail.pastDispenses = medOrderDispenseDetail.pastDispenses.concat(dispenseDataForMedOrder);
      }
      medOrderDispenseDetail = this.setDispensedWastedAmountAndUnit(medOrderDispenseDetail);
      medOrderDispenseDetail.configuredUnit = medOrderDispenseDetail.dispensedAmount > 0 ?
        medOrderDispenseDetail.dispensedUnit : detail.doseUnit;
      medOrderDispenseDetailList.push(medOrderDispenseDetail);
    });
    return medOrderDispenseDetailList;
  }

  private setDispensedWastedAmountAndUnit(medOrderDispenseDetail: MedOrderDispenseDetail): MedOrderDispenseDetail {
    medOrderDispenseDetail.dispensedAmount = 0;
    medOrderDispenseDetail.dispensedUnit = medOrderDispenseDetail.detail.doseUnit;
    if (medOrderDispenseDetail.pastDispenses && medOrderDispenseDetail.pastDispenses.length > 0) {
      medOrderDispenseDetail.pastDispenses.forEach(pastDispense => {
        if (pastDispense.dispenseUnit === medOrderDispenseDetail.detail.doseUnit) {
          medOrderDispenseDetail.dispensedAmount += pastDispense.medOrderDispenseAmount;
          medOrderDispenseDetail.wastedAmount += pastDispense.medOrderWasteAmount;
          medOrderDispenseDetail.dispensedUnit = pastDispense.dispenseUnit;
        } else {
          const convertedDispenseAmount = ConversionHelper.convertQuantityByUnitName(pastDispense.medOrderDispenseAmount,
            pastDispense.dispenseUnit, medOrderDispenseDetail.detail.doseUnit);
          const convertedWasteAmount = ConversionHelper.convertQuantityByUnitName(pastDispense.medOrderWasteAmount,
            pastDispense.dispenseUnit, medOrderDispenseDetail.detail.doseUnit);
          if (convertedDispenseAmount > 0 || pastDispense.medOrderDispenseAmount === 0) {
            medOrderDispenseDetail.dispensedAmount += convertedDispenseAmount;
            medOrderDispenseDetail.wastedAmount += convertedWasteAmount;
            medOrderDispenseDetail.dispensedUnit = medOrderDispenseDetail.detail.doseUnit;
          } else {
            medOrderDispenseDetail.dispensedAmount += pastDispense.medOrderDispenseAmount;
            medOrderDispenseDetail.wastedAmount += pastDispense.medOrderWasteAmount;
            medOrderDispenseDetail.dispensedUnit = pastDispense.dispenseUnit;
          }
        }
      });
    }
    return medOrderDispenseDetail;
  }

  private cacheInventoryItems(inventoryItemSummaries: InventoryItemSummary[]): void {
    if (
      !this.isInventoryCached ||
      this.stationInfoService.isInventoryCacheExpired
    ) {
      let localInventoryItems = this.convertDataModel(inventoryItemSummaries);
      localInventoryItems = localInventoryItems.sort(
        (i1, i2) => {
          if (i1.name < i2.name) {
            return -1;
          } else if (i1.name === i2.name) {
            return 0;
          } else {
            return 1;
          }
        },
      );
      this.inventoryItems.next(localInventoryItems);
      this.isInventoryCached = true;
      this.stationInfoService.isInventoryCacheExpired = false;
    }
  }

  private updateSummaryDispenseItem(dispenseItems: DispenseItem[]): void {
    const newDispenseItems: DispenseItem[] = [];
    AbsgArrayHelper.each(dispenseItems, item => { // Only want lots with quantity > 0
      if (item.fromCabinetQuantity > 0 || item.fromUnderHoodQuantity > 0) {
        let dispenseItem = newDispenseItems.find(i => i.inventoryItem.id === item.inventoryItem.id);
        const product = item.inventoryItem.inventoryItemProducts[0];
        if (!dispenseItem) {
          dispenseItem = AbsgArrayHelper.cloneDeep(item);
          dispenseItem.ndcLotList = [new NdcDetail(item.inventoryItem.ndc, [new LotDetail(product.lotNumber, product.expirationDate)])];
          dispenseItem.hasItemBeenDispensed = true;
          newDispenseItems.push(dispenseItem);
        } else {
          dispenseItem.ndcLotList[0].lotDetails.push(new LotDetail(product.lotNumber, product.expirationDate));
          dispenseItem.fromCabinetQuantity = CalculationHelper.add(dispenseItem.fromCabinetQuantity, item.fromCabinetQuantity);
          dispenseItem.fromUnderHoodQuantity = CalculationHelper.add(dispenseItem.fromUnderHoodQuantity, item.fromUnderHoodQuantity);
          dispenseItem.wasteQuantity =
            CalculationHelper.add(dispenseItem.wasteQuantity, (!AbsgArrayHelper.isNil(item.wasteQuantity) ? item.wasteQuantity : 0));
          dispenseItem.toCabinetQuantity = CalculationHelper.add(dispenseItem.toCabinetQuantity, item.toCabinetQuantity);
          dispenseItem.toUnderHoodQuantity = CalculationHelper.add(dispenseItem.toUnderHoodQuantity, item.toUnderHoodQuantity);
        }
      }
    });
    AbsgArrayHelper.each(newDispenseItems, item => { // Only include items with quantity for summary and receipts and labels
      if (item.fromCabinetQuantity > 0 || item.fromUnderHoodQuantity > 0) {
        this.addUpdateDispenseItem(item);
        // update Med Order dispense item
        // not required as the only property used (quantity selceted) is not updated and rest are not utilized
      } else {
        this.removeDispenseItemByInventoryItemId(item.inventoryItem.id);
      }
    });
    // update med order dispenseItem properties to display value on summary page
    this.updateMedOrderDispenseItems();
  }

  updateMedOrderDispenseItems(): void {
    // update dispense info and merge lots
    AbsgArrayHelper.forEach(this.medOrderDispenseItems, (modi: DispenseSelectedGroup) => {
      const dispenseItemList: DispenseItem[] = [];
      AbsgArrayHelper.forEach(modi.dispenseItems, (dispItem: DispenseItem) => {
        // find dispItem in this.dispenseItems
        const mainDispenseItem = this.dispenseItems.find(d => d.inventoryItem.id === dispItem.inventoryItem.id);
        if (mainDispenseItem) {
          dispItem.wasteQuantity = mainDispenseItem.wasteQuantity;
          dispItem.fromCabinetQuantity = mainDispenseItem.fromCabinetQuantity;
          dispItem.toCabinetQuantity = mainDispenseItem.toCabinetQuantity;
          dispItem.fromUnderHoodQuantity = mainDispenseItem.fromUnderHoodQuantity;
          dispItem.toUnderHoodQuantity = mainDispenseItem.toUnderHoodQuantity;
        }
        // grouping by inventory item id assures that same spiid (regular and patient owned) won't merge
        const dispenseItem = dispenseItemList.find(dil => dil.inventoryItem.id === dispItem.inventoryItem.id);
        if (!dispenseItem) {
          dispenseItemList.push(dispItem);
        } else {
          dispenseItem.inventoryItem.quantitySelected += dispItem.inventoryItem.quantitySelected;
        }
      });
      modi.dispenseItems = AbsgArrayHelper.cloneDeep(dispenseItemList);
    });
  }

  isMultiMar(item: DispenseItem): boolean {
    return ((item.inventoryItem && item.inventoryItem.quantitySelected > 0) &&
      (!item.dispenseQuantity || item.dispenseQuantity === 0) &&
      this.fetchMedOrdersByDispenseItem(item).length > 1);
  }

  getBinItems(filteredDispenseItems: DispenseItem[]): DispenseItem[] {
    const dispenseItems: DispenseItem[] = [];
    AbsgArrayHelper.each(filteredDispenseItems, currentDispenseItem => {
      const binInventoryItems = this.getContainerNDCsWithQtyOnHand(currentDispenseItem.inventoryItem.getContainerId());
      AbsgArrayHelper.each(binInventoryItems, currentBinInventoryItem => {
        let dispenseItem = new DispenseItem();
        dispenseItem = AbsgArrayHelper.cloneDeep(currentDispenseItem);
        dispenseItem.inventoryItem = AbsgArrayHelper.cloneDeep(currentBinInventoryItem);
        dispenseItem.inventoryItem.quantitySelected = currentDispenseItem.inventoryItem.quantitySelected;
        dispenseItem.inventoryItem.dispenseTypeId = currentBinInventoryItem.dispenseTypeId;
        dispenseItem.inventoryItem.isMoveToRemaining = !this.isTypeReturn(dispenseItem);
        dispenseItem.inventoryItem.quantitySelectedFromCabinet = currentDispenseItem.inventoryItem.quantitySelectedFromCabinet;
        dispenseItem.inventoryItem.quantitySelectedFromUnderhood = currentDispenseItem.inventoryItem.quantitySelectedFromUnderhood;
        dispenseItem.inventoryItem.inventoryItemProducts = currentBinInventoryItem.inventoryItemProducts;
        dispenseItems.push(dispenseItem);
      });
    });
    return dispenseItems;
  }

  getLastAttendingPhysicianDetails(): Observable<LastAttendingPhysicianDetails> {
    return this.dispenseApi.getLastAttendingPhysicianDetails();
  }

  getPatientMedOrderStatuses(refreshData = false): Observable<PatientMedOrderStatus[]> {
    const orderName = this.isScheduledOrderVendor ? 'Scheduled Order' : 'Med Order';
    const futureMedOrderOptions = this.futureMedOrderOptions;
    return this.emrService.getPatientMedOrders(futureMedOrderOptions?.clinicId, futureMedOrderOptions?.medOrderDate, refreshData).pipe(
      take(1),
      concatMap((patientsWithMedOrders: PatientsWithMedOrders[]) => {
        if (AbsgArrayHelper.isEmpty(patientsWithMedOrders)) {
          return of([]);
        } else {
          return this.medOrderHelperService.getMedOrderDispenseStatusesForPatientsWithMedOrders(patientsWithMedOrders).pipe(
            take(1),
            catchError((error) => {
              this.handleError(error);
              this.nimAlertService.showErrorAlert(`Error retrieving ${orderName} dispense status information`);
              return of([]);
            }),
          );
        }
      }),
      catchError((error) => {
        this.handleError(error);
        this.nimAlertService.showErrorAlert(`Error retrieving Patient ${orderName} information`);
        return of([]);
      }),
    );
  }

  getComprehensiveApiPrintingFlag(transactionIds: number[]): Observable<CheckFeatureResponse> {
    const request: CheckFeatureRequest = {
      featureId: FeatureEnum.ComprehensiveApiPrinting,
      transactionIdList: transactionIds,
    };
    return this.transactionApi.getFeatureFlagByTransaction(request).pipe(
      take(1),
      catchError((error) => {
        this.handleError(error);
        this.nimAlertService.showErrorAlert(`Error retrieving Printing Feature Flag for transactionIds: ${JSON.stringify(transactionIds)}`);
        return of(null);
      }),
    );
  }

  private triggerDispenseMultiLotEvent(transactionRequest: TransactionRequest, dispenseItems: DispenseItem[]): Observable<Transaction> {
    return this.dispenseApi.addDispenseItemMultiLotWithPointInTimeData(transactionRequest)
      .pipe(take(1),
        concatMap(transaction => {
          this.currentTransaction.next(transaction);
          return this.processTransactionDetails(transaction, dispenseItems);
        }));
  }

  // Not my favorite way to do this, but it will work temporarily until the remodel
  private searchInventoryItems(inventoryItems: InventoryItem[], query: string): InventoryItem[] {
    query = query.toLowerCase();
    const filteredArray = AbsgArrayHelper.filter(inventoryItems, item => this.isItemMatch(query, item));
    return filteredArray;
  }

  private isItemMatch(filterValue: string, item: InventoryItem): boolean {
    if (AbsgArrayHelper.isNil(filterValue) || filterValue === '') {
      return true;
    }

    return this.searchRegularFields(item, filterValue) || PatientOwnedItemHelper.searchPatientFields(item.patientInfo, filterValue);
  }
  private searchRegularFields(item: InventoryItem, filterValue: string): boolean {
    return ['alternateName', 'name', 'genericName', 'ndc', 'itemId', 'gcnSequenceNumber', 'searchItemJCode', 'jCode', 'altJCode', 'binAltJCode']
      .some(key => item[key] && item[key].toLowerCase().includes(filterValue));
  }

  private getSupplierProductInclusionIds(inventoryItems: InventoryItemSummary[]): number[] {
    if (!inventoryItems) {
      return [];
    }
    const ids = inventoryItems
      .filter(position => position?.supplierProductInclusionId)
      .map(position => position.supplierProductInclusionId);

    return ids;
  }

  private determineNWSLDispenseItem(item: DispenseItem, extractedAccessPoint: ExtractedAccessPoint): boolean {
    // Selected only Dispense Items that match target LocationType.
    // All Items must filter on locationType (AKA AccessPoint Type)

    if (item.dispenseLocationType !== extractedAccessPoint.doorType) {
      return false;
    }

    if (AbsgArrayHelper.isNil(extractedAccessPoint) || AbsgArrayHelper.isNil(extractedAccessPoint.doorNumber) || extractedAccessPoint.doorNumber <= 0) {
      return false;
    }

    const invItem = item.inventoryItem;
    if (item.dispenseLocationType !== LocationType.UnderHood) {
      const doorName = item.inventoryItem.storagePosition.parentContainerName.toLowerCase();
      if (!this.isSameDoorNumber(doorName, extractedAccessPoint.doorNumber)) { return false; }

      if (extractedAccessPoint.isLAD) {
        if (Number(invItem.storagePosition.name.split('-')[1].split(' ')[2]) !== extractedAccessPoint.ladNumber) {
          return false;
        }
      }
    }

    if ((invItem.multiLotYN && this.hasItemMultipleLots(invItem)) ||
      (invItem.isBin && this.getContainerNDCsWithQtyOnHand(invItem.getContainerId()).length > 1)) {
      return false;
    }

    if (item.inventoryItem.verifyQOH) {
      return false;
    }

    if (item.dispenseLocationType !== LocationType.UnderHood && item.fromCabinetQuantity === 0 && item.toCabinetQuantity === 0) {
      return false;
    }

    if (item.dispenseLocationType === LocationType.UnderHood && item.fromUnderHoodQuantity === 0) {
      return false;
    }

    if (this.fetchMedOrdersByDispenseItem(item).length > 1) {
      return false;
    }

    return true;
  }

  private determineVerifyQohDispenseItems(item: DispenseItem, extractedAccessPoint: ExtractedAccessPoint): boolean {

    if (item.dispenseLocationType !== extractedAccessPoint.doorType) {
      return false;
    }

    if (AbsgArrayHelper.isNil(extractedAccessPoint) || AbsgArrayHelper.isNil(extractedAccessPoint.doorNumber) || extractedAccessPoint.doorNumber <= 0) {
      return false;
    }

    if (item.dispenseLocationType !== LocationType.UnderHood) {
      const doorName = item.inventoryItem.storagePosition.parentContainerName.toLowerCase();
      if (!this.isSameDoorNumber(doorName, extractedAccessPoint.doorNumber)) { return false; }

      if (extractedAccessPoint.isLAD) {
        if (Number(item.inventoryItem.storagePosition.name.split('-')[1].split(' ')[2]) !== extractedAccessPoint.ladNumber) {
          return false;
        }
      }
    }

    if (!item.inventoryItem.verifyQOH) {
      return false;
    }
    return true;
  }

  private determineMultiLotOrBinOrMultiMarDispenseItems(item: DispenseItem, extractedAccessPoint: ExtractedAccessPoint): boolean {
    if (item.dispenseLocationType !== extractedAccessPoint.doorType) {
      return false;
    }

    if (AbsgArrayHelper.isNil(extractedAccessPoint) || AbsgArrayHelper.isNil(extractedAccessPoint.doorNumber) || extractedAccessPoint.doorNumber <= 0) {
      return false;
    }

    const inventoryItem = item.inventoryItem;
    if (extractedAccessPoint.isLAD) {
      if (inventoryItem.storagePosition.name !== extractedAccessPoint.accessPointName) {
        return false;
      }
    } else {
      const doorName = item.inventoryItem.storagePosition.parentContainerName.toLowerCase();
      if (!this.isSameDoorNumber(doorName, extractedAccessPoint.doorNumber)) { return false; }
    }

    return (inventoryItem.multiLotYN) ||
      (inventoryItem.isBin && this.getContainerNDCsWithQtyOnHand(inventoryItem.getContainerId()).length > 1) ||
      this.isMultiMar(item);
  }

  private determineVerifyQohDispenseItem(item: DispenseItem, accessPointNumber: number,
    targetLocationType: LocationType): boolean {

    if (item.dispenseLocationType !== targetLocationType) {
      return false;
    }

    if (AbsgArrayHelper.isNil(accessPointNumber) || accessPointNumber <= 0) {
      return false;
    }

    const doorName = item.inventoryItem.storagePosition.parentContainerName.toLowerCase();
    if (!this.isSameDoorNumber(doorName, accessPointNumber)) { return false; }

    if (!item.inventoryItem.verifyQOH) {
      return false;
    }
    return true;
  }

  private determineNonVerifySingleLotDispenseItem(item: DispenseItem, accessPointNumber: number,
    targetLocationType: LocationType): boolean {
    // Selected only Dispense Items that match target LocationType.
    // All Items must filter on locationType (AKA AccessPoint Type)

    if (item.dispenseLocationType !== targetLocationType) {
      return false;
    }

    // Virtual and Under Hood are dispensed as a set and not based on their numbers.
    if (AbsgArrayHelper.isNil(accessPointNumber) || accessPointNumber <= 0) {
      return false;
    }

    const doorName = item.inventoryItem.storagePosition.parentContainerName.toLowerCase();

    if (doorName === 'vpm' || doorName === 'lpm') {
      if (accessPointNumber !== this.vpmLpmDoorNumber) { return false; }
    } else if (Number(doorName.split(' ')[1]) !== accessPointNumber) {
      return false;
    }

    if (item.inventoryItem.multiLotYN) {
      return false;
    }

    if (item.inventoryItem.verifyQOH) {
      return false;
    }

    if (item.dispenseLocationType !== LocationType.UnderHood) {
      if (item.fromCabinetQuantity === 0) {
        return false;
      }
    } else {
      if (item.fromUnderHoodQuantity === 0) {
        return false;
      }
    }
    return true;
  }

  private isSameDoorNumber(doorName: string, accessPointNumber: number): boolean {
    if (doorName === 'vpm' || doorName === 'lpm') {
      if (accessPointNumber !== this.vpmLpmDoorNumber) { return false; }
    } else if (Number(doorName.split(' ')[1]) !== accessPointNumber) {
      return false;
    }
    return true;
  }

  private listen(): void {
    this.ipcRenderer?.on(CleanupEvents.cleanupRequest, () =>
      this.cleanupRequest(),
    );
  }

  private cleanupRequest(): void {
    this.clearDispenseData();
  }

  private findProduct(supplierProductInclusionId: number, products: ProductDetail[]): ProductDetail {
    const findResults = AbsgArrayHelper.filter(products, product => product.supplierProductInclusionId === supplierProductInclusionId);
    if (findResults.length > 0) {
      return findResults[0];
    }
    return null;
  }

  private convertDataModel(products: InventoryItemSummary[]): InventoryItem[] {
    const itemArray = new Array<InventoryItem>();

    let localInventoryItem: InventoryItem = null;
    // Perform conversion to Inventory items
    products.forEach(product => {

      localInventoryItem = new InventoryItem();
      localInventoryItem.name = '';
      localInventoryItem.genericName = '';
      localInventoryItem.id = product.inventoryItemId;
      localInventoryItem.supplierProductInclusionId =
        product.supplierProductInclusionId;

      // Storage Position
      localInventoryItem.storagePosition = this.stationInfoService.getPositionById(
        product.positionContainerId,
      );

      localInventoryItem.supplierProductInclusionId =
        product.supplierProductInclusionId;
      localInventoryItem.lastRestockDate = new Date(DateTimeHelper.formatDateTime(product.lastRestockDate));
      localInventoryItem.lastDispenseDate = new Date(DateTimeHelper.formatDateTime(product.lastDispenseDate));
      localInventoryItem.lastAuditDate = new Date(DateTimeHelper.formatDateTime(product.lastAuditDate));
      localInventoryItem.inventoryItemProducts = product.inventoryItemProducts;
      // Set Default Values, before adding actual values.
      localInventoryItem.quantityInCabinet = 0;
      // UNDERHOOD: Load initial quantities from Inventory Service
      localInventoryItem.quantityUnderHood = 0;

      localInventoryItem.inventoryItemProducts = product.inventoryItemProducts;
      if (product.inventoryItemProducts) {
        product.inventoryItemProducts.forEach(inventoryItemProduct => {
          localInventoryItem.quantityInCabinet =
            localInventoryItem.quantityInCabinet +
            inventoryItemProduct.lotQuantity;
          localInventoryItem.quantityUnderHood =
            localInventoryItem.quantityUnderHood +
            inventoryItemProduct.lotQuantityUnderhood;
        });
      }
      localInventoryItem.pendingOverstockQuantity = product.pendingOverstockQuantity;
      localInventoryItem.pendingQuantity = product.pendingQuantity;
      localInventoryItem.patientId = product.patientId;
      localInventoryItem.patientOwnedItemTypeId = product.patientOwnedItemTypeId;
      const patientOwnedItemType = this.patientOwnedItemTypeList.find(x => x.listInclusionId === product.patientOwnedItemTypeId);
      localInventoryItem.patientOwnedItemTypeShortName = patientOwnedItemType?.shortName;
      localInventoryItem.patientOwnedItemTypeFullName = patientOwnedItemType?.name;

      itemArray.push(localInventoryItem);
    });
    return itemArray;
  }

  private saveDiagnosisCodesAsActivities(
    transaction: Transaction,
    diagnosisCodes: DiagnosisCode[],
  ): Observable<Transaction> {
    let activities: Activity[] = [];
    AbsgArrayHelper.each(diagnosisCodes, diagnosisCode => {
      const activity: Activity = {
        activityType: this.commonDataService.getActivityTypeById(InventoryActivityTypes.DispenseDiagnosisCode),
        diagnosisCode: diagnosisCode.code,
        diagnosisDescription: diagnosisCode.name?.substring(0, 99),
        transactionId: transaction.transactionId,
      };
      activities.push(activity);
    });
    if (activities.length > 0) {
      return this.activityApi.addActivities(activities).pipe(
        map(returnedActivities => {
          activities = [];
          activities.concat(returnedActivities);
          return transaction;
        }),
        catchError((error) => {
          this.handleError(error);
          this.nimAlertService.showErrorAlert('Error Saving Diagnosis Codes');
          return of().pipe(map(() => null));
        }));
    }
    return of(transaction);
  }

  private getInventoryDisplayInfo(item: InventoryItem): ItemDisplay {
    const displayRow = new InventoryDisplay();
    displayRow.displayName = item.getDisplayName();
    displayRow.alternateName = item.alternateName;
    displayRow.isBin = item.isBin;
    displayRow.defaultInventoryItem = item;
    displayRow.defaultInventoryItem.displayJCode = item.displayJCode;
    displayRow.defaultInventoryItem.is340B = item.is340B;
    displayRow.defaultInventoryItem.isBuyIn = item.isBuyIn;
    const containerId = item.getContainerId();
    const inventoryItemsInBin = this.getContainerNDCsWithQtyOnHand(containerId);
    displayRow.displaySingleNdcSingleLot = !item.isBin || (inventoryItemsInBin.length === 1 && this.hasItemSingleLotWithQoh(item));
    const patientInfo = this.getPatientInfoById(item?.patientId);
    if (patientInfo) {
      displayRow.defaultInventoryItem.patientInfo = patientInfo;
      const patientOwnedItemType = this.patientOwnedItemTypeList.find(x => x.listInclusionId === item.patientOwnedItemTypeId);
      displayRow.defaultInventoryItem.patientOwnedItemTypeShortName = patientOwnedItemType?.shortName;
      displayRow.defaultInventoryItem.patientOwnedItemTypeFullName = patientOwnedItemType?.name;
    }
    if (item.isBin) {
      const megList: BinProductDetail[] = this.getBinGroups();
      const megItem = (megList && megList.length > 0) ?
        megList.find((meg: BinProductDetail) => meg.supplierProductInclusionId === item.meiParentId) : undefined;
      if (megItem) {
        displayRow.defaultInventoryItem.gcnSequenceNumber = megItem.gcnSequenceNumber;
        displayRow.defaultInventoryItem.binAltJCode = megItem.altJCode;
        displayRow.binAltJCode = megItem.altJCode;
        displayRow.binAltBillableUnit = megItem.altBConversionFactor;
        displayRow.binJCodes = [];
        AbsgArrayHelper.each(AbsgArrayHelper.uniq(megItem.jCodes), code => displayRow.binJCodes.push(code.toLowerCase()));
        displayRow.defaultInventoryItem.emrMedIds = AbsgArrayHelper.uniq(AbsgArrayHelper.flatMap(inventoryItemsInBin, (item) => item.emrMedIds ?? []));
      } else {
        displayRow.binJCodes = [item.jCode?.toLowerCase()];
        displayRow.defaultInventoryItem.emrMedIds = item.emrMedIds;
      }
    }
    return displayRow;
  }

  hasItemSingleLotWithQoh(item: InventoryItem): boolean {
    if (item.inventoryItemProducts.length === 1 && (item.inventoryItemProducts[0].lotQuantity > 0 || item.inventoryItemProducts[0].lotQuantityUnderhood > 0)) {
      return true;
    }
    return false;
  }

  getPatientInfoById(patientId: number): PatientLite {
    let patientInfoById: PatientLite;
    if ((!AbsgArrayHelper.isNil(this.mappedPatientList))) {
      patientInfoById = AbsgArrayHelper.find(this.mappedPatientList, x => x?.patientId === patientId);
      if (patientInfoById) {
        return patientInfoById;
      }
    }
    return patientInfoById;
  }

  private createEventDetailInfo(dispenseItem: DispenseItem, itemId?: string, supplierProductInclusionId?: number): EventDetailInfo {
    if (dispenseItem === null || dispenseItem.inventoryItem === null) {
      throw new Error('CreateEventDetailInfo will Not accept a Null DispenseItem or InventoryItem');
    }
    let lot;
    const invItemProductIdentifier = dispenseItem.inventoryItem.inventoryItemProducts
      .filter(x => x.lotQuantity > 0 || x.lotQuantityUnderhood > 0)[0];

    if (!AbsgArrayHelper.isNil(invItemProductIdentifier)) {
      lot = invItemProductIdentifier;
    }

    const eventDetail: EventDetailInfo = {
      dispenseConversionFactor: dispenseItem.inventoryItem.dispenseUOMConversionFactor,
      inventoryConversionFactor: dispenseItem.inventoryItem.inventoryUOMConversionFactor,
      purchaseConversionFactor: dispenseItem.inventoryItem.purchaseUOMConversionFactor,
      inventoryItemId: invItemProductIdentifier ? invItemProductIdentifier.inventoryItemId :
        dispenseItem.inventoryItem.id,
      supplierProductItemId: itemId ? itemId : dispenseItem.inventoryItem.itemId,
      invItemProductIdentifierId: lot !== undefined ? lot.invItemProductIdentifierId : '',
      itemMaxAfter: 0,
      itemMaxBefore: 0,
      itemMinAfter: 0,
      itemMinBefore: 0,
      lotExpirationDate: lot !== undefined ? lot.expirationDate : '',
      lotNumber: lot !== undefined ? lot.lotNumber : '',
      // Use 'peerTransactionInventoryDetailId' when tie-ing associated transaction details together,
      //  Examples: Dispense from UnderHood and Cabinet
      //  Dispense from Cabinet and Waste Remainder
      peerTransactionInventoryDetailId: null,
      pendingQtyOnHandAfter: dispenseItem.inventoryItem.pendingQuantity,
      pendingQtyOnHandBefore: dispenseItem.inventoryItem.pendingQuantity,
      annexTransferQtyOnHandAfter: dispenseItem.inventoryItem.pendingAnnexTransferQuantity,
      annexTransferQtyOnHandBefore: dispenseItem.inventoryItem.pendingAnnexTransferQuantity,
      productDescription: dispenseItem.inventoryItem.tradeName,
      serialNumber: lot !== undefined ? lot.serialNumber : '',
      supplierProductInclusionId: dispenseItem.inventoryItem.supplierProductInclusionId,
      witnessUserId: dispenseItem.inventoryItem.witnessUserId,
      witnessUserName: dispenseItem.inventoryItem.witnessUserName,
      witnessTransactionIds: dispenseItem.witnessTransactionIds,
      positionContainerId: dispenseItem.inventoryItem.storagePosition.id,
      spiIdForItemOrdered: supplierProductInclusionId ? supplierProductInclusionId : 0,
      patientId: dispenseItem.inventoryItem.patientId,
      patientOwnedItemTypeId: dispenseItem.inventoryItem.patientOwnedItemTypeId,
    };
    if (dispenseItem.inventoryItem.requiredNote && dispenseItem.inventoryItem.requiredNote.note) {
      eventDetail.detailNotes = dispenseItem.inventoryItem.requiredNote.note;
      dispenseItem.inventoryItem.requiredNote.note = '';
    } else {
      eventDetail.detailNotes = '';
    }
    return this.populateAutoReOrder(eventDetail, dispenseItem.inventoryItem);
  }

  private mapToDispenseSnapshotDomainModel(dispenseSnapshotViewModel: DispenseSnapshotViewModel): DispenseSnapshot {
    const dispenseSnapshot: DispenseSnapshot = {
      allergies: dispenseSnapshotViewModel.allergies,
      attendingPhysicianFirstName: dispenseSnapshotViewModel.attendingPhysicianFirstName,
      attendingPhysicianLastName: dispenseSnapshotViewModel.attendingPhysicianLastName,
      clinicAddressLine1: dispenseSnapshotViewModel.clinicAddressLine1,
      clinicCity: dispenseSnapshotViewModel.clinicCity,
      clinicName: dispenseSnapshotViewModel.clinicName,
      clinicPhoneNumber: dispenseSnapshotViewModel.clinicPhoneNumber,
      clinicState: dispenseSnapshotViewModel.clinicState,
      clinicZipCode: dispenseSnapshotViewModel.clinicZipCode,
      dateOfBirth: dispenseSnapshotViewModel.dateOfBirth,
      dispenseSnapshotId: dispenseSnapshotViewModel.dispenseSnapshotId,
      dispensedByUserId: dispenseSnapshotViewModel.dispensedByUserId,
      dispensedByUserName: dispenseSnapshotViewModel.dispensedByUserName,
      dispensedByFirstName: dispenseSnapshotViewModel.dispensedByFirstName,
      dispensedByLastName: dispenseSnapshotViewModel.dispensedByLastName,
      patientFirstName: dispenseSnapshotViewModel.patientFirstName,
      patientIdNumber: dispenseSnapshotViewModel.patientId,
      patientLastName: dispenseSnapshotViewModel.patientLastName,
      practiceName: dispenseSnapshotViewModel.practiceName,
      stationHost: dispenseSnapshotViewModel.stationHost,
      transactionId: dispenseSnapshotViewModel.transactionId,
    };

    dispenseSnapshot.dispenseDetailSnapshot
      = this.mapToDispenseDetailsSnapshotDomainModel(dispenseSnapshotViewModel.dispenseDetailSnapshot);
    return dispenseSnapshot;
  }

  private mapToDispenseDetailsSnapshotDomainModel(dispenseDetailsSnapshotsViewModel: Array<DispenseDetailSnapshotViewModel>): Array<DispenseDetailSnapshot> {
    const dispenseDetailsSnapshots: Array<DispenseDetailSnapshot> = [];
    AbsgArrayHelper.each(dispenseDetailsSnapshotsViewModel, (dispenseDetailsSnapshotViewModel) => {
      const dispenseDetailSnapshotViewModel: DispenseDetailSnapshot = {
        transactionId: dispenseDetailsSnapshotViewModel.transactionId,
        supplierProductInclusionId: dispenseDetailsSnapshotViewModel.supplierProductInclusionId,
        gcnSequenceNumber: dispenseDetailsSnapshotViewModel.gcnSequenceNumber,
        genericName: dispenseDetailsSnapshotViewModel.genericName,
        displayName: dispenseDetailsSnapshotViewModel.displayName,
        inventoryUnitOfMeasureConversionFactor: dispenseDetailsSnapshotViewModel.inventoryUnitOfMeasureConversionFactor,
        inventoryUnitOfMeasureDisplay: dispenseDetailsSnapshotViewModel.inventoryUnitOfMeasureDisplay,
        volumeUnitOfMeasureConversionFactor: dispenseDetailsSnapshotViewModel.volumeUnitOfMeasureConversionFactor,
        volumeUnitOfMeasureDisplay: dispenseDetailsSnapshotViewModel.volumeUnitOfMeasureDisplay,
        purchaseUnitOfMeasureConversionFactor: dispenseDetailsSnapshotViewModel.purchaseUnitOfMeasureConversionFactor,
        infuseNotes1: dispenseDetailsSnapshotViewModel.infuseNotes1,
        infuseNotes2: dispenseDetailsSnapshotViewModel.infuseNotes2,
        infuseNotes3: dispenseDetailsSnapshotViewModel.infuseNotes3,
        dispenseDetailSnapshotId: dispenseDetailsSnapshotViewModel.dispenseDetailSnapshotId,
        unitNdc: dispenseDetailsSnapshotViewModel.unitNdc,
        packageNdc: dispenseDetailsSnapshotViewModel.packageNdc,
        tradeName: dispenseDetailsSnapshotViewModel.tradeName,
        isPracticeFavorite: dispenseDetailsSnapshotViewModel.isPracticeFavorite,
        isDiscontinued: dispenseDetailsSnapshotViewModel.isDiscontinued,
        formularyTypeId: dispenseDetailsSnapshotViewModel.formularyTypeId,
        alternateName: dispenseDetailsSnapshotViewModel.alternateName,
        purchaseUnitOfMeasureDisplay: dispenseDetailsSnapshotViewModel.purchaseUnitOfMeasureDisplay,
        itemId: dispenseDetailsSnapshotViewModel.itemId,
        isRefrigerated: dispenseDetailsSnapshotViewModel.isRefrigerated,
        bundleLabelCode: dispenseDetailsSnapshotViewModel.bundleLabelCode,
        bundleLabelName: dispenseDetailsSnapshotViewModel.bundleLabelName,
        positionName: dispenseDetailsSnapshotViewModel.positionName,
        accessPointName: dispenseDetailsSnapshotViewModel.accessPointName,
        autoPrintLabel: dispenseDetailsSnapshotViewModel.autoPrintLabel,
        autoPrintReceipt: dispenseDetailsSnapshotViewModel.autoPrintReceipt,
        displayInfusionNotesOnLabel: dispenseDetailsSnapshotViewModel.displayInfusionNotesOnLabel,
        displayInfusionNotesOnReceipt: dispenseDetailsSnapshotViewModel.displayInfusionNotesOnReceipt,
        dispenseUnitOfMeasureDisplay: dispenseDetailsSnapshotViewModel.dispenseUnitOfMeasureDisplay,
        dispenseUnitOfMeasureConversionFactor: dispenseDetailsSnapshotViewModel.dispenseUnitOfMeasureConversionFactor,
        productGroupTypeId: dispenseDetailsSnapshotViewModel.productGroupTypeId,
        dispenseTypeId: dispenseDetailsSnapshotViewModel.dispenseTypeId,
        dispenseTypeName: dispenseDetailsSnapshotViewModel.dispenseTypeName,
        displayRequiredNoteOnLabel: dispenseDetailsSnapshotViewModel.displayRequiredNoteOnLabel,
        displayRequiredNoteOnReceipt: dispenseDetailsSnapshotViewModel.displayRequiredNoteOnReceipt,
        isWitnessRequired: dispenseDetailsSnapshotViewModel.isWitnessRequired,
        instructions: dispenseDetailsSnapshotViewModel.instructions,
        displayInstructionsOnLabel: dispenseDetailsSnapshotViewModel.displayInstructionsOnLabel,
        route: dispenseDetailsSnapshotViewModel.route,
        displayRouteOnLabel: dispenseDetailsSnapshotViewModel.displayRouteOnLabel,
        displayFluidOnLabel: dispenseDetailsSnapshotViewModel.displayFluidOnLabel,
        displayDurationOnLabel: dispenseDetailsSnapshotViewModel.displayDurationOnLabel,
        patientId: dispenseDetailsSnapshotViewModel.patientId,
        patientOwnedItemTypeId: dispenseDetailsSnapshotViewModel.patientOwnedItemTypeId,
        patientOwnedPatientIdNumber: dispenseDetailsSnapshotViewModel.patientOwnedPatientIdNumber,
        patientOwnedPatientFirstName: dispenseDetailsSnapshotViewModel.patientOwnedPatientFirstName,
        patientOwnedPatientLastName: dispenseDetailsSnapshotViewModel.patientOwnedPatientLastName,
        patientOwnedPatientDOB: dispenseDetailsSnapshotViewModel.patientOwnedPatientDOB,
        patientOwnedItemTypeLongName: dispenseDetailsSnapshotViewModel.patientOwnedItemTypeLongName,
        patientOwnedItemTypeShortName: dispenseDetailsSnapshotViewModel.patientOwnedItemTypeShortName,
        isItem505b2: dispenseDetailsSnapshotViewModel.isItem505b2,
        isBuyIn: dispenseDetailsSnapshotViewModel.isBuyIn,
        is340B: dispenseDetailsSnapshotViewModel.is340B,
      };

      dispenseDetailsSnapshots.push(dispenseDetailSnapshotViewModel);

    });
    return dispenseDetailsSnapshots;
  }

  private createTransactionRequest(dispenseSnapshotViewModel: DispenseSnapshotViewModel): TransactionRequest {
    // TODO - update TransactionRequest to add medOrderInfo
    const clinicDetails = this.practiceClinicInfo.getValue();
    const stationInfo = this.stationInfoService.station.getValue();
    // TODO: replace when expanding pasts OSC to other suppliers
    const targetSupplierPartyId = 1;
    const dispenseSnapshot = this.mapToDispenseSnapshotDomainModel(dispenseSnapshotViewModel);
    const dispenseTransactionType =
      this.commonDataService.getTransactionTypeById(InventoryTransactionTypes.Dispense);
    const transactionRequest: TransactionRequest = {
      transactionEvent: {
        clinicName: clinicDetails.clinicName,
        clinicPartyId: clinicDetails.clinicId,
        createdById: 1,
        fromStationContainerId: stationInfo.id,
        fromStationName: stationInfo.name,
        itemDetails: [] as EventDetailInfo[],
        practicePartyName: clinicDetails.practiceName,
        practiceDomain: clinicDetails.domain,
        patientId: this.selectedPatient.id,
        practicePartyId: clinicDetails.practiceId,
        serviceDate: this.serviceDate,
        transactionTypeId: dispenseTransactionType.transactionTypeId,
        transactionTypeName: dispenseTransactionType.name,
        facilityId: stationInfo.facilityId,
        targetSupplierPartyId,
        stationAccountNumber: stationInfo.acctNumber,
      },
      dispenseSnapshot,
    };

    if (this.selectedAttendingPhysician) {
      transactionRequest.transactionEvent.attendingPhysicianPartyId = this.selectedAttendingPhysician.id;
    }
    if (this.currentTransaction.getValue()) {
      const current = this.currentTransaction.getValue();
      transactionRequest.transactionEvent.transactionId = current.transactionId;
    }
    return transactionRequest;
  }
  private isTypeReturn(dispenseItem: DispenseItem): boolean {
    return dispenseItem.inventoryItem.dispenseTypeId === DispenseTypes.ReturnToPosition ||
      dispenseItem.inventoryItem.dispenseTypeId === DispenseTypes.ReturnToUnderHood;
  }

  private getSupplierProductInclusionId(item: InventoryItem): number {
    return item.isBinned() ? item.meiParentId : item.supplierProductInclusionId;
  }

  private BuildMedOrderDetail(medOrderDetail: MedOrderDetail): ApiInventoryMedOrderDetail {
    const modd: ApiInventoryMedOrderDetail = {
      componentName: medOrderDetail.componentName,
      medOrderDetailId: medOrderDetail.medOrderDetailId,
      emrOrderId: medOrderDetail.emrOrderId,
    };
    return modd;
  }

  private BuildApiInventoryItem(inventoryItem: InventoryItem, identifierdId: number, wastedAmount: number): APIInventoryItem {
    const ii: APIInventoryItem = {
      inventoryItemId: inventoryItem.id,
      jCode: inventoryItem.jCode,
      volumeUnit: inventoryItem.volumeUnit,
      supplierProductInclusionId: inventoryItem.supplierProductInclusionId,
      dispenseUOMConversionFactor: inventoryItem.dispenseUOMConversionFactor,
      volumeUOMConversionFactor: inventoryItem.volumeUOMConversionFactor,
      billableUnitOfMeasureSize: inventoryItem.billableUnitOfMeasureSize,
      altBillableUnitOfMeasureSize: inventoryItem.altBillableUnitOfMeasureSize,
      quantitySelected: inventoryItem.quantitySelected,
      isBin: inventoryItem.isBin,
      meiParentId: inventoryItem.meiParentId,
      patientId: inventoryItem.patientId,
      patientOwnedItemTypeId: inventoryItem.patientOwnedItemTypeId,
      invItemProductIdentifierID: identifierdId,
      wasteQuantity: wastedAmount,
    };
    return ii;
  }

  private BuildBinGroupInventoryItems(inventoryDisplay: InventoryDisplay): BinGroupInventoryItem {
    const bii: BinGroupInventoryItem = {
      binAltBillableUnit: inventoryDisplay.binAltBillableUnit,
      binAltJCode: inventoryDisplay.binAltJCode,
      binJCodes: inventoryDisplay.binJCodes,
      meiParentId: inventoryDisplay.defaultInventoryItem.meiParentId,
      isBin: inventoryDisplay.isBin,
    };
    return bii;
  }
}
